import React, { useState, useEffect, useRef } from 'react';
import { Home, MessageCircle, Target, Users, Settings, ArrowRight, Check, Plus, Send, AlertTriangle, Sparkles, Calendar, TrendingUp, Sunrise, Moon, Zap, HelpCircle, ClipboardCheck, Mic, MicOff, User, Briefcase, ChevronRight, CheckCircle2, Clock, Eye, EyeOff, Shield, Lock, Info, X, LogOut, UserPlus, Search, Inbox, Award, BookOpen, FileText, Bell, MessageSquare, Flag, RefreshCw, Share2, Download, Archive } from 'lucide-react';
import { auth as authApi, vault as vaultApi, ai as aiApi, coaches as coachesApi, messages as messagesApi, signals as signalsApi, notifications as notificationsApi, setAccessToken, clearAccessToken } from './api/client';

// ============================================================================
// CLIENT-SIDE VAULT CRYPTO
// Tier 1 data (check-ins, chat, reflections) is encrypted before leaving the
// device. Key is derived from the user's password — server stores ciphertext only.
// ============================================================================

const PBKDF2_ITERATIONS = 210000;
const enc = new TextEncoder();
const dec = new TextDecoder();
const toB64 = (buf) => btoa(String.fromCharCode(...new Uint8Array(buf)));
const fromB64 = (str) => Uint8Array.from(atob(str), c => c.charCodeAt(0));

async function deriveVaultKey(password, saltB64) {
  const salt = fromB64(saltB64);
  const baseKey = await crypto.subtle.importKey(
    'raw', enc.encode(password), { name: 'PBKDF2' }, false, ['deriveKey']
  );
  return crypto.subtle.deriveKey(
    { name: 'PBKDF2', salt, iterations: PBKDF2_ITERATIONS, hash: 'SHA-256' },
    baseKey, { name: 'AES-GCM', length: 256 }, false, ['encrypt', 'decrypt']
  );
}

async function encryptVaultData(data, key) {
  const iv = crypto.getRandomValues(new Uint8Array(12));
  const encrypted = await crypto.subtle.encrypt(
    { name: 'AES-GCM', iv }, key, enc.encode(JSON.stringify(data))
  );
  const ivHex = Array.from(iv).map(b => b.toString(16).padStart(2,'0')).join('');
  return { encryptedData: toB64(encrypted), iv: ivHex, authTag: '' };
}

async function decryptVaultData(encryptedData, ivHex, key) {
  const iv = new Uint8Array(ivHex.match(/.{2}/g).map(b => parseInt(b, 16)));
  const decrypted = await crypto.subtle.decrypt(
    { name: 'AES-GCM', iv }, key, fromB64(encryptedData)
  );
  return JSON.parse(dec.decode(decrypted));
}

function generateSaltB64() {
  return toB64(crypto.getRandomValues(new Uint8Array(16)));
}

// Vault salt stored in sessionStorage — survives page refresh, cleared on tab close
const SALT_SESSION_KEY = 'yc_vsalt';

function getOrCreateVaultSalt() {
  let salt = sessionStorage.getItem(SALT_SESSION_KEY);
  if (!salt) { salt = generateSaltB64(); sessionStorage.setItem(SALT_SESSION_KEY, salt); }
  return salt;
}

// ============================================================================
// UNREAD COUNT — fetched from server
// ============================================================================

async function fetchUnreadCount(linkId) {
  if (!linkId) return 0;
  try {
    const data = await messagesApi.getThread(linkId);
    const userId = window.__yc_userId;
    return (data.messages || []).filter(m => m.to === userId && !m.read).length;
  } catch { return 0; }
}

// ============================================================================
// SEED COACHES — shown before real coaches exist on the platform
// ============================================================================

// ============================================================================
// SERVER-BACKED HELPERS
// These replace the old localStorage functions.
// Components call these; they delegate to the server API.
// ============================================================================

// Simple in-memory cache for messages during a session
const _msgCache = {};
const _notesCache = {};
const _contextCache = {};
const _storeCache = { coachPool: [], briefPool: [], coachClientLinks: {} };

function loadStore() { return _storeCache; }
function saveStore(data) { Object.assign(_storeCache, data); }

async function getMessages(linkIdOrCoachEmail, clientEmail) {
  // If called with a real UUID linkId, use it directly
  const linkId = clientEmail ? `${linkIdOrCoachEmail}::${clientEmail}` : linkIdOrCoachEmail;
  // Don't call server with a non-UUID linkId
  if (!linkId || linkId.includes('::') || linkId.includes('@')) {
    return _msgCache[linkId] || [];
  }
  try {
    const result = await messagesApi.getThread(linkId);
    _msgCache[linkId] = result.messages || [];
    return _msgCache[linkId];
  } catch {
    return _msgCache[linkId] || [];
  }
}

async function appendMessage(linkIdOrCoachEmail, clientEmailOrNull, msg) {
  const linkId = clientEmailOrNull
    ? `${linkIdOrCoachEmail}::${clientEmailOrNull}`
    : linkIdOrCoachEmail;
  // Guard against non-UUID linkIds
  if (!linkId || linkId.includes('::') || linkId.includes('@')) {
    console.warn('appendMessage: invalid linkId', linkId);
    return null;
  }
  try {
    const result = await messagesApi.send(linkId, {
      body: msg.body,
      kind: msg.kind || 'nudge',
      recipientId: msg.to,
    });
    const newMsg = result.message || { ...msg, id: 'm_' + Date.now(), createdAt: new Date().toISOString(), read: false };
    _msgCache[linkId] = [...(_msgCache[linkId] || []), newMsg];
    return newMsg;
  } catch (e) {
    console.error('appendMessage error:', e.message);
    return null;
  }
}

async function markMessagesRead(linkIdOrCoachEmail, clientEmailOrNull, forRecipient) {
  const linkId = clientEmailOrNull
    ? `${linkIdOrCoachEmail}::${clientEmailOrNull}`
    : linkIdOrCoachEmail;
  if (!linkId || linkId.includes('::') || linkId.includes('@')) return;
  try {
    await messagesApi.markRead(linkId);
    if (_msgCache[linkId]) {
      _msgCache[linkId] = _msgCache[linkId].map(m =>
        m.to === forRecipient ? { ...m, read: true } : m
      );
    }
  } catch {}
}

async function getSessionNotes(linkIdOrCoachEmail, clientEmail) {
  const linkId = clientEmail ? `${linkIdOrCoachEmail}::${clientEmail}` : linkIdOrCoachEmail;
  if (!linkId || linkId.includes('::') || linkId.includes('@')) return _notesCache[linkId] || [];
  try {
    const result = await coachesApi.getSessionNotes(linkId);
    _notesCache[linkId] = result.notes || [];
    return _notesCache[linkId];
  } catch {
    return _notesCache[linkId] || [];
  }
}

async function saveSessionNote(linkIdOrCoachEmail, clientEmailOrContent, content) {
  // Support both (linkId, content) and (coachEmail, clientEmail, content)
  let linkId, noteContent;
  if (content === undefined) {
    linkId = linkIdOrCoachEmail;
    noteContent = clientEmailOrContent;
  } else {
    linkId = `${linkIdOrCoachEmail}::${clientEmailOrContent}`;
    noteContent = content;
  }
  if (!linkId || linkId.includes('::') || linkId.includes('@')) return null;
  try {
    const result = await coachesApi.saveSessionNote(linkId, noteContent);
    const note = result.note || { id: 'n_' + Date.now(), content: noteContent, createdAt: new Date().toISOString() };
    _notesCache[linkId] = [...(_notesCache[linkId] || []), note];
    return note;
  } catch (e) {
    console.error('saveSessionNote error:', e.message);
    return null;
  }
}

async function getSharedContext(clientEmailOrLinkId, coachEmailOrNull) {
  const linkId = coachEmailOrNull ? `${coachEmailOrNull}::${clientEmailOrLinkId}` : clientEmailOrLinkId;
  if (!linkId || linkId.includes('::') || linkId.includes('@')) return _contextCache[linkId] || null;
  try {
    const result = await coachesApi.getSharedContext(linkId);
    _contextCache[linkId] = result.sharedContext;
    return result.sharedContext;
  } catch {
    return _contextCache[linkId] || null;
  }
}

async function setSharedContext(clientEmailOrLinkId, coachEmailOrNull, payload) {
  const linkId = coachEmailOrNull ? `${coachEmailOrNull}::${clientEmailOrLinkId}` : clientEmailOrLinkId;
  if (!linkId || linkId.includes('::') || linkId.includes('@')) return;
  try {
    await coachesApi.saveSharedContext(linkId, {
      agenda: payload.sessionAgenda,
      flaggedTopics: payload.flaggedTopics || [],
    });
    _contextCache[linkId] = { ...payload, sharedAt: new Date().toISOString() };
  } catch (e) {
    console.error('setSharedContext error:', e.message);
  }
}

// window.confirm replacement that doesn't trigger no-restricted-globals
function confirmAction(msg) {
  // eslint-disable-next-line no-restricted-globals
  return window.confirm(msg);
}



const PERSONAS = {
  mentor: {
    id: 'mentor', name: 'The Mentor', tagline: 'Wise, measured, strategic',
    description: 'A trusted advisor who helps you see the bigger picture. Patient, thoughtful, grounded in experience.',
    accent: '#8B6F47', glyph: '◈',
    axes: { directness: 0.6, warmth: 0.6, structure: 0.8, energy: 0.4 },
    systemPrompt: `You are The Mentor, a wise and measured life coach within the YourCoach app. You speak like a trusted advisor with decades of considered experience. You are strategic, patient, and help the user see patterns and bigger pictures they might miss. You ask thoughtful questions before giving advice. You never use clinical or therapeutic language. You keep responses concise (2-4 sentences usually) and substantive. You never diagnose mental health conditions. If someone mentions serious distress, self-harm, or crisis, gently direct them to professional support (Samaritans 116 123 in the UK, or 111). You use British English.`,
    greetings: {
      morning: (name) => `Morning, ${name}. Let's set the day properly.`,
      afternoon: (name) => `Afternoon, ${name}. How's the day shaping up?`,
      evening: (name) => `Evening, ${name}. Time to look back.`,
    },
  },
  friend: {
    id: 'friend', name: 'The Friend', tagline: 'Warm, casual, honest',
    description: 'Your switched-on mate who happens to be in your corner. Validating when you need it, honest when you need that more.',
    accent: '#C97B5C', glyph: '◉',
    axes: { directness: 0.5, warmth: 0.9, structure: 0.3, energy: 0.7 },
    systemPrompt: `You are The Friend, a warm and casual life coach within the YourCoach app. You talk like a genuine, switched-on mate. You validate feelings but you're also honest — you'll gently call someone out if they're making excuses. You use conversational language, contractions, and the occasional bit of humour. You never use clinical or therapeutic jargon. Keep responses short and real (2-4 sentences). You never diagnose mental health conditions. If someone mentions serious distress, self-harm, or crisis, gently direct them to professional support (Samaritans 116 123 in the UK, or 111). You use British English.`,
    greetings: {
      morning: (name) => `Hey ${name}, morning! Ready to crack on?`,
      afternoon: (name) => `${name}! How's your day going?`,
      evening: (name) => `Alright ${name}, how'd the day treat you?`,
    },
  },
  sergeant: {
    id: 'sergeant', name: 'The Drill Sergeant', tagline: 'Direct, tough love, no excuses',
    description: 'For when you need a kick, not a hug. Blunt, demanding, and relentlessly focused on action over feelings.',
    accent: '#6B4423', glyph: '▲',
    axes: { directness: 1.0, warmth: 0.2, structure: 0.9, energy: 0.9 },
    systemPrompt: `You are The Drill Sergeant, a direct and no-nonsense life coach within the YourCoach app. You cut through excuses. You focus on action, accountability, and results. You're blunt but not cruel — you're tough because you believe the user can handle it and is capable of more. You never coddle. Short, punchy responses (1-3 sentences). You never use clinical or therapeutic language. You never diagnose mental health conditions. CRITICAL: If someone mentions serious distress, self-harm, or crisis, drop the tough-guy act entirely — speak plainly and warmly, and direct them to professional support (Samaritans 116 123 in the UK, or 111). You use British English.`,
    greetings: {
      morning: (name) => `${name}. Up. Let's go.`,
      afternoon: (name) => `${name}. Status check.`,
      evening: (name) => `${name}. Report in.`,
    },
  },
  guide: {
    id: 'guide', name: 'The Guide', tagline: 'Reflective, curious, coaching-led',
    description: 'Asks more than they answer. Helps you reach your own conclusions through powerful questions.',
    accent: '#5C7A6B', glyph: '◯',
    axes: { directness: 0.3, warmth: 0.7, structure: 0.6, energy: 0.5 },
    systemPrompt: `You are The Guide, a reflective coaching-led life coach within the YourCoach app. You follow a professional coaching style: you ask powerful, open questions far more than you give advice. You help the user reach their own conclusions. You're curious, present, and non-judgemental. You never use clinical or therapeutic language — this is coaching, not therapy. Responses should usually be a brief reflection plus one good question (2-4 sentences total). You never diagnose mental health conditions. If someone mentions serious distress, self-harm, or crisis, step out of coaching mode and gently direct them to professional support (Samaritans 116 123 in the UK, or 111). You use British English.`,
    greetings: {
      morning: (name) => `Good morning, ${name}. What would be most useful to explore?`,
      afternoon: (name) => `Hello ${name}. What's present for you right now?`,
      evening: (name) => `Evening, ${name}. What's worth reflecting on?`,
    },
  },
};

// Seed coaches — always present in coachPool for matching
const SEED_COACHES = [
  { id: 'seed_c1', name: 'Rachel Okonkwo', credential: 'ICF-ACC Certified', specialism: 'Career transitions, executive coaching', bio: 'Fifteen years coaching mid-career professionals through pivots. Direct but compassionate.', axes: { directness: 0.7, warmth: 0.7, structure: 0.8, energy: 0.6 }, rate: 85, seed: true },
  { id: 'seed_c2', name: 'James Patel', credential: 'EMCC Senior Practitioner', specialism: 'Habit formation, wellbeing, burnout', bio: 'Former NHS clinician turned coach. Grounded approach built on behavioural science.', axes: { directness: 0.4, warmth: 0.8, structure: 0.7, energy: 0.4 }, rate: 95, seed: true },
  { id: 'seed_c3', name: 'Sofia Marchetti', credential: 'ICF-PCC Certified', specialism: 'Creative professionals, life design', bio: 'Works with creatives and founders who want to build lives, not just careers.', axes: { directness: 0.5, warmth: 0.9, structure: 0.4, energy: 0.8 }, rate: 75, seed: true },
];

function matchScore(a, b) {
  const keys = ['directness', 'warmth', 'structure', 'energy'];
  const dist = Math.sqrt(keys.reduce((sum, k) => sum + Math.pow(a[k] - b[k], 2), 0));
  return 1 - (dist / 2);
}

function getAllCoaches(store) {
  return [...SEED_COACHES, ...(store.coachPool || [])];
}

function recommendPersona(userAxes) {
  return Object.values(PERSONAS).map(p => ({ ...p, score: matchScore(p.axes, userAxes) })).sort((a, b) => b.score - a.score)[0];
}

// Tiered matching: excellent (0.85+), good (0.7-0.85), stretch (0.55-0.7), poor (<0.55)
function matchQuality(score) {
  if (score >= 0.85) return 'excellent';
  if (score >= 0.7) return 'good';
  if (score >= 0.55) return 'stretch';
  return 'poor';
}

function tieredMatches(userAxes, coaches) {
  const scored = coaches.map(c => ({ ...c, score: matchScore(c.axes, userAxes), quality: matchQuality(matchScore(c.axes, userAxes)) }));
  return {
    excellent: scored.filter(c => c.quality === 'excellent').sort((a, b) => b.score - a.score),
    good: scored.filter(c => c.quality === 'good').sort((a, b) => b.score - a.score),
    stretch: scored.filter(c => c.quality === 'stretch').sort((a, b) => b.score - a.score),
    all: scored.sort((a, b) => b.score - a.score),
  };
}

// ============================================================================
// GUIDED FLOWS
// ============================================================================

const FLOWS = {
  morning: {
    id: 'morning', title: 'Morning check-in', subtitle: 'Set the tone for today', icon: Sunrise, estMinutes: 3,
    steps: [
      { prompt: 'How are you arriving this morning?', subprompt: 'Be honest — this shapes the rest.', type: 'choice',
        options: [
          { id: 'fresh', label: 'Fresh & ready' }, { id: 'ok', label: 'OK, bit sluggish' },
          { id: 'flat', label: 'Flat or low energy' }, { id: 'anxious', label: 'Wound up or anxious' },
          { id: 'rough', label: 'Rough — didn\'t sleep well' },
        ], key: 'arrival' },
      { prompt: 'What\'s the ONE thing that must happen today?', subprompt: 'If nothing else gets done, this would still be a win.', type: 'text', placeholder: 'Your most important thing...', key: 'oneThing' },
      { prompt: 'What could get in the way?', subprompt: 'Naming it makes it easier to sidestep.', type: 'text', placeholder: 'Distractions, resistance, other demands...', key: 'obstacle', optional: true },
      { prompt: 'How will you protect time for your habits?', subprompt: 'Tiny plan beats big ambition.', type: 'text', placeholder: 'When and where...', key: 'habitPlan', optional: true },
    ],
    coachClosingPrompt: (answers) => `The user just completed their morning check-in. They're feeling ${answers.arrival}, and their one priority today is: "${answers.oneThing}". ${answers.obstacle ? `They're watching out for: ${answers.obstacle}. ` : ''}Respond with a short, personalised send-off in your persona's voice. 2 sentences max.`,
  },
  evening: {
    id: 'evening', title: 'Evening reflection', subtitle: 'Close the day with intention', icon: Moon, estMinutes: 4,
    steps: [
      { prompt: 'How was today overall?', type: 'scale5', key: 'mood' },
      { prompt: 'What went well today?', subprompt: 'However small. Name at least one thing.', type: 'text', placeholder: 'A win, a moment...', key: 'went_well' },
      { prompt: 'What was harder than expected?', type: 'text', placeholder: 'Something that tested you...', key: 'harder', optional: true },
      { prompt: 'What did today teach you?', subprompt: 'One takeaway, not an essay.', type: 'text', placeholder: 'A lesson, a pattern...', key: 'learned', optional: true },
      { prompt: 'What are you carrying into tomorrow?', type: 'text', placeholder: 'A thought, a commitment, a release...', key: 'carry', optional: true },
    ],
    coachClosingPrompt: (answers) => `The user just finished their evening reflection. Their day was rated ${answers.mood}/2. Wins: "${answers.went_well}". ${answers.harder ? `Challenges: ${answers.harder}. ` : ''}${answers.learned ? `They learned: ${answers.learned}. ` : ''}Respond with a warm, reflective close in your persona's voice. 2 sentences max.`,
  },
  stuck: {
    id: 'stuck', title: 'I\'m stuck', subtitle: 'Work through something using the GROW model', icon: HelpCircle, estMinutes: 6,
    steps: [
      { prompt: 'What\'s on your mind?', subprompt: 'The situation, challenge or decision you\'re stuck on.', type: 'text', placeholder: 'Describe what\'s going on...', key: 'situation' },
      { prompt: 'What would a good outcome look like?', subprompt: 'Specifically — not "feel better" but what, concretely?', type: 'text', placeholder: 'The result you actually want...', key: 'goal' },
      { prompt: 'What have you already tried or considered?', type: 'text', placeholder: 'Things you\'ve done or thought about...', key: 'reality', optional: true },
      { prompt: 'If there were no obstacles, what would you do?', subprompt: 'Dream a little.', type: 'text', placeholder: 'The ideal move...', key: 'options' },
      { prompt: 'What\'s one small step you could take in the next 24 hours?', subprompt: 'Small enough that you\'ll actually do it.', type: 'text', placeholder: 'A tiny first action...', key: 'will' },
    ],
    coachClosingPrompt: (answers) => `The user just worked through the GROW model. Situation: "${answers.situation}". Desired outcome: "${answers.goal}". Ideal action: "${answers.options}". Next step: "${answers.will}". Respond with affirming encouragement in your persona's voice. 3 sentences max.`,
  },
  weekly: {
    id: 'weekly', title: 'Weekly review', subtitle: 'Zoom out and reset', icon: ClipboardCheck, estMinutes: 8,
    steps: [
      { prompt: 'How has this week been overall?', type: 'scale5', key: 'mood' },
      { prompt: 'What are you most proud of this week?', type: 'text', placeholder: 'A win, a stretch...', key: 'proud' },
      { prompt: 'Where did you fall short?', subprompt: 'Honest but not brutal. Data, not judgement.', type: 'text', placeholder: 'Something you\'d do differently...', key: 'short', optional: true },
      { prompt: 'What\'s the most important thing for next week?', type: 'text', placeholder: 'The priority above all others...', key: 'next_priority' },
      { prompt: 'What do you need less of?', type: 'text', placeholder: 'Less of...', key: 'less', optional: true },
      { prompt: 'What support do you need?', type: 'text', placeholder: 'Support from...', key: 'support', optional: true },
    ],
    coachClosingPrompt: (answers) => `The user just completed their weekly review. Proud of: "${answers.proud}". Fell short on: "${answers.short || 'nothing mentioned'}". Next priority: "${answers.next_priority}". Respond with a reflective summary in your persona's voice. 3-4 sentences.`,
  },
};

// ============================================================================
// SAFETY
// ============================================================================

const DISTRESS_PATTERNS = [/suicid/i, /kill myself/i, /end it all/i, /self[- ]?harm/i, /hurt myself/i, /want to die/i, /no reason to live/i, /can'?t go on/i, /can'?t cope/i];
function detectDistress(text) { return text && DISTRESS_PATTERNS.some(p => p.test(text)); }

// ============================================================================
// THEME
// ============================================================================

const FONTS = `@import url('https://fonts.googleapis.com/css2?family=Fraunces:ital,opsz,wght@0,9..144,300;0,9..144,400;0,9..144,500;0,9..144,600;1,9..144,400&family=Inter:wght@300;400;500;600&display=swap');`;
const theme = {
  bg: '#F4EFE6', bgDeep: '#EBE3D4', ink: '#2A2420', inkSoft: '#5C544C', inkFaint: '#8A8178',
  paper: '#FBF8F2', line: '#D9CFBE', accent: '#8B6F47', accentDeep: '#5C4422',
  danger: '#A0382C', success: '#5C7A6B', info: '#4A6B7A',
};

// ============================================================================
// DICTATION HOOK
// ============================================================================

function useDictation(onTranscript) {
  const [isListening, setIsListening] = useState(false);
  const [supported, setSupported] = useState(false);
  const recognitionRef = useRef(null);

  useEffect(() => {
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SR) { setSupported(false); return; }
    setSupported(true);
    const recognition = new SR();
    recognition.continuous = true; recognition.interimResults = true; recognition.lang = 'en-GB';
    let finalTranscript = '';
    recognition.onresult = (event) => {
      let interim = '';
      for (let i = event.resultIndex; i < event.results.length; i++) {
        const t = event.results[i][0].transcript;
        if (event.results[i].isFinal) finalTranscript += t + ' ';
        else interim += t;
      }
      onTranscript(finalTranscript + interim);
    };
    recognition.onend = () => { setIsListening(false); finalTranscript = ''; };
    recognition.onerror = () => { setIsListening(false); finalTranscript = ''; };
    recognitionRef.current = recognition;
    return () => { try { recognition.stop(); } catch {} };
  }, []);

  const toggle = () => {
    if (!recognitionRef.current) return;
    if (isListening) { recognitionRef.current.stop(); setIsListening(false); }
    else { try { recognitionRef.current.start(); setIsListening(true); } catch { setIsListening(false); } }
  };
  return { isListening, supported, toggle };
}

function DictableText({ value, onChange, placeholder, rows = 3, autoFocus, style }) {
  const baseTextRef = useRef(value || '');
  const handleTranscript = (transcript) => {
    const newText = (baseTextRef.current + ' ' + transcript).trim().replace(/\s+/g, ' ');
    onChange(newText);
  };
  const { isListening, supported, toggle } = useDictation(handleTranscript);
  const handleMicClick = () => { if (!isListening) baseTextRef.current = value || ''; toggle(); };

  return (
    <div style={{ position: 'relative' }}>
      <textarea value={value || ''} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} autoFocus={autoFocus} rows={rows}
        style={{ fontFamily: '"Fraunces", serif', fontSize: 18, background: theme.paper, border: `1px solid ${isListening ? theme.accent : theme.line}`, padding: '14px 52px 14px 16px', color: theme.ink, outline: 'none', width: '100%', resize: 'none', lineHeight: 1.5, boxSizing: 'border-box', transition: 'border-color 200ms', ...style }} />
      {supported && (
        <button onClick={handleMicClick} type="button"
          style={{ position: 'absolute', top: 10, right: 10, width: 34, height: 34, background: isListening ? theme.accent : 'transparent', border: `1px solid ${isListening ? theme.accent : theme.line}`, borderRadius: '50%', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', color: isListening ? theme.paper : theme.inkSoft, transition: 'all 200ms' }}>
          {isListening ? <MicOff size={15} /> : <Mic size={15} />}
        </button>
      )}
      {isListening && (
        <div style={{ position: 'absolute', bottom: -20, right: 0, fontSize: 11, color: theme.accent, display: 'flex', alignItems: 'center', gap: 4 }}>
          <span style={{ width: 6, height: 6, borderRadius: '50%', background: theme.accent, animation: 'pulse 1s infinite' }} /> Listening...
        </div>
      )}
    </div>
  );
}

// ============================================================================
// HELP / INFO COMPONENTS
// ============================================================================

function HelpTip({ children, title }) {
  const [open, setOpen] = useState(false);
  return (
    <div style={{ position: 'relative', display: 'inline-block' }}>
      <button onClick={(e) => { e.stopPropagation(); setOpen(!open); }}
        style={{ background: 'transparent', border: 'none', color: theme.inkFaint, cursor: 'pointer', padding: 2, display: 'inline-flex' }}>
        <Info size={13} />
      </button>
      {open && (
        <>
          <div onClick={() => setOpen(false)} style={{ position: 'fixed', inset: 0, zIndex: 50 }} />
          <div style={{ position: 'absolute', top: 22, left: -8, zIndex: 51, width: 260, background: theme.ink, color: theme.paper, padding: '12px 14px', fontSize: 12, lineHeight: 1.5, boxShadow: '0 8px 24px rgba(0,0,0,0.2)' }}>
            {title && <div style={{ fontFamily: '"Fraunces", serif', fontSize: 13, marginBottom: 6, fontWeight: 500 }}>{title}</div>}
            {children}
          </div>
        </>
      )}
    </div>
  );
}

function InfoCallout({ children, icon, tone = 'info' }) {
  const colors = {
    info: { bg: '#F0F2F4', border: theme.info, text: theme.ink },
    success: { bg: '#F0F4F1', border: theme.success, text: theme.ink },
    accent: { bg: theme.bg, border: theme.accent, text: theme.ink },
  }[tone];
  return (
    <div style={{ background: colors.bg, borderLeft: `3px solid ${colors.border}`, padding: '12px 14px', display: 'flex', gap: 10, alignItems: 'flex-start', fontSize: 13, lineHeight: 1.5, color: colors.text, marginBottom: 12 }}>
      {icon && <div style={{ color: colors.border, flexShrink: 0, marginTop: 1 }}>{icon}</div>}
      <div>{children}</div>
    </div>
  );
}

// ============================================================================
// AUTH — Login & Signup
// ============================================================================

function AuthScreen({ onAuth, onCreate, message }) {
  const [mode, setMode] = useState('welcome'); // welcome | login | signup
  const [accountType, setAccountType] = useState(null);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPw, setConfirmPw] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const handleLogin = async () => {
    setError(''); setBusy(true);
    try {
      const result = await authApi.login({ email, password });
      let kdfSalt = result.user.kdfSalt;
      if (!kdfSalt) {
        const vaultResult = await vaultApi.get().catch(() => ({ kdfSalt: null, vault: null }));
        kdfSalt = vaultResult.kdfSalt || generateSaltB64();
        console.log('[login] kdfSalt from vault GET:', kdfSalt ? kdfSalt.slice(0,8)+'...' : 'NONE');
      }
      const vaultKey = await deriveVaultKey(password, kdfSalt);
      let data = {};
      try {
        const vaultResult = await vaultApi.get();
        if (vaultResult.vault?.encryptedData) {
          const raw = await decryptVaultData(vaultResult.vault.encryptedData, vaultResult.vault.iv, vaultKey);
          const accountType = result.user.accountType;
          data = accountType === 'coach'
            ? (normaliseCoachData(raw) || {})
            : (normaliseClientData(raw) || {});
        }
      } catch (e) {
        console.error('[YourCoach] vault decrypt error:', e.message);
        data = {};
      }
      window.__yc_userId = result.user.id;
      window.__yc_accessToken = result.accessToken;
      onAuth({ id: result.user.id, email: email.toLowerCase(), accountType: result.user.accountType, data, vaultKey, kdfSalt });
    } catch (e) {
      setError(e.message || 'Login failed.');
    } finally { setBusy(false); }
  };

  const handleSignup = async () => {
    setError('');
    if (!email.match(/^[^\s@]+@[^\s@]+\.[^\s@]+$/)) { setError('Enter a valid email.'); return; }
    if (password.length < 10) { setError('Password must be at least 10 characters.'); return; }
    if (!/[A-Z]/.test(password)) { setError('Password must contain an uppercase letter.'); return; }
    if (!/[0-9]/.test(password)) { setError('Password must contain a number.'); return; }
    if (password !== confirmPw) { setError("Passwords don't match."); return; }

    setBusy(true);
    try {
      const result = await authApi.signup({ email, password, accountType });
      // Server generates and stores the kdfSalt — use it from the response
      const kdfSalt = result.user.kdfSalt || generateSaltB64();
      const vaultKey = await deriveVaultKey(password, kdfSalt);
      window.__yc_userId = result.user.id;
      window.__yc_accessToken = result.accessToken;
      onCreate({ id: result.user.id, email: email.toLowerCase(), accountType, data: {}, vaultKey, kdfSalt });
    } catch (e) {
      setError(e.message || 'Signup failed.');
    } finally { setBusy(false); }
  };

  if (mode === 'welcome') {
    return (
      <div style={{ minHeight: '100vh', background: theme.bg, display: 'flex', flexDirection: 'column', padding: '2rem 1.5rem' }}>
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center', maxWidth: 440, width: '100%', margin: '0 auto' }}>
          <div style={{ textAlign: 'center', marginBottom: '3rem' }}>
            <div style={{ fontSize: 48, fontFamily: '"Fraunces", serif', color: theme.accent, marginBottom: 16, letterSpacing: '0.1em' }}>◈ ◉ ▲ ◯</div>
            <h1 style={{ fontFamily: '"Fraunces", serif', fontWeight: 400, fontSize: 'clamp(2.25rem, 8vw, 3rem)', color: theme.ink, margin: 0, marginBottom: 10, letterSpacing: '-0.03em' }}>YourCoach</h1>
            <p style={{ fontFamily: '"Fraunces", serif', fontStyle: 'italic', fontSize: 16, color: theme.inkSoft, margin: 0, lineHeight: 1.5 }}>A coach in your pocket.<br />A human one on call.</p>
          </div>

          {message && (
            <div style={{ background: '#FEF8EE', border: `1px solid ${theme.accent}`, borderLeft: `3px solid ${theme.accent}`, padding: '12px 14px', marginBottom: 16, fontSize: 13, color: theme.ink, lineHeight: 1.5 }}>
              {message}
            </div>
          )}

          <button onClick={() => setMode('login')}
            style={{ background: theme.ink, color: theme.paper, border: 'none', padding: '14px', fontSize: 14, cursor: 'pointer', marginBottom: 10, letterSpacing: '0.02em' }}>
            Log in
          </button>
          <button onClick={() => setMode('signup')}
            style={{ background: 'transparent', color: theme.ink, border: `1px solid ${theme.ink}`, padding: '14px', fontSize: 14, cursor: 'pointer', marginBottom: 24, letterSpacing: '0.02em' }}>
            Create account
          </button>

          <div style={{ background: theme.paper, border: `1px solid ${theme.line}`, borderLeft: `3px solid ${theme.success}`, padding: '12px 14px', display: 'flex', gap: 10, alignItems: 'flex-start' }}>
            <Shield size={16} color={theme.success} style={{ flexShrink: 0, marginTop: 2 }} />
            <div style={{ fontSize: 12, color: theme.inkSoft, lineHeight: 1.5 }}>
              Your data is encrypted on this device with your password. Nobody — not even us — can read it without you logging in.
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (mode === 'signup' && !accountType) {
    return (
      <div style={{ minHeight: '100vh', background: theme.bg, padding: '2rem 1.5rem' }}>
        <button onClick={() => setMode('welcome')} style={{ background: 'transparent', border: 'none', color: theme.inkSoft, fontSize: 14, cursor: 'pointer', padding: 0, marginBottom: 20 }}>← Back</button>
        <div style={{ maxWidth: 440, margin: '2rem auto 0' }}>
          <div style={{ fontSize: 11, letterSpacing: '0.25em', textTransform: 'uppercase', color: theme.inkFaint, marginBottom: 8 }}>New account</div>
          <h2 style={{ fontFamily: '"Fraunces", serif', fontWeight: 400, fontSize: '2rem', color: theme.ink, margin: 0, marginBottom: '1.5rem', letterSpacing: '-0.02em' }}>I'm joining as...</h2>

          <button onClick={() => setAccountType('client')}
            style={{ background: theme.paper, border: `1px solid ${theme.line}`, padding: '20px', cursor: 'pointer', textAlign: 'left', marginBottom: 12, display: 'flex', alignItems: 'center', gap: 16, width: '100%' }}>
            <div style={{ width: 44, height: 44, background: theme.bgDeep, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', color: theme.accent, flexShrink: 0 }}><User size={20} /></div>
            <div style={{ flex: 1 }}>
              <div style={{ fontFamily: '"Fraunces", serif', fontSize: 18, color: theme.ink }}>Someone seeking coaching</div>
              <div style={{ fontSize: 12, color: theme.inkSoft }}>Daily support and accountability</div>
            </div>
            <ChevronRight size={18} color={theme.inkFaint} />
          </button>

          <button onClick={() => setAccountType('coach')}
            style={{ background: theme.paper, border: `1px solid ${theme.line}`, padding: '20px', cursor: 'pointer', textAlign: 'left', display: 'flex', alignItems: 'center', gap: 16, width: '100%' }}>
            <div style={{ width: 44, height: 44, background: theme.bgDeep, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', color: theme.accent, flexShrink: 0 }}><Briefcase size={20} /></div>
            <div style={{ flex: 1 }}>
              <div style={{ fontFamily: '"Fraunces", serif', fontSize: 18, color: theme.ink }}>A practising coach</div>
              <div style={{ fontSize: 12, color: theme.inkSoft }}>Manage clients, take sessions</div>
            </div>
            <ChevronRight size={18} color={theme.inkFaint} />
          </button>
        </div>
      </div>
    );
  }

  // Login or signup form
  return (
    <div style={{ minHeight: '100vh', background: theme.bg, padding: '2rem 1.5rem' }}>
      <button onClick={() => { setMode('welcome'); setAccountType(null); setError(''); }} style={{ background: 'transparent', border: 'none', color: theme.inkSoft, fontSize: 14, cursor: 'pointer', padding: 0, marginBottom: 20 }}>← Back</button>
      <div style={{ maxWidth: 440, margin: '2rem auto 0' }}>
        <div style={{ fontSize: 11, letterSpacing: '0.25em', textTransform: 'uppercase', color: theme.inkFaint, marginBottom: 8 }}>
          {mode === 'login' ? 'Welcome back' : `New ${accountType} account`}
        </div>
        <h2 style={{ fontFamily: '"Fraunces", serif', fontWeight: 400, fontSize: '2rem', color: theme.ink, margin: 0, marginBottom: '1.5rem', letterSpacing: '-0.02em' }}>
          {mode === 'login' ? 'Log in to YourCoach' : 'Create your account'}
        </h2>

        <div style={{ marginBottom: 12 }}>
          <label style={{ fontSize: 11, letterSpacing: '0.1em', textTransform: 'uppercase', color: theme.inkSoft, marginBottom: 6, display: 'block' }}>Email</label>
          <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} autoComplete="email" autoFocus
            style={{ width: '100%', background: theme.paper, border: `1px solid ${theme.line}`, padding: '12px 14px', fontSize: 15, color: theme.ink, outline: 'none', boxSizing: 'border-box' }} />
        </div>

        <div style={{ marginBottom: 12 }}>
          <label style={{ fontSize: 11, letterSpacing: '0.1em', textTransform: 'uppercase', color: theme.inkSoft, marginBottom: 6, display: 'flex', alignItems: 'center', gap: 6 }}>
            Password
            <HelpTip title="Your password"><span>This password encrypts your data locally. If you forget it, there's no recovery — your data can't be decrypted without it. In the full product, we'd add secure recovery options.</span></HelpTip>
          </label>
          <div style={{ position: 'relative' }}>
            <input type={showPassword ? 'text' : 'password'} value={password} onChange={(e) => setPassword(e.target.value)} autoComplete={mode === 'login' ? 'current-password' : 'new-password'}
              onKeyDown={(e) => { if (e.key === 'Enter' && !busy) { if (mode === 'login') { handleLogin(); } else if (confirmPw) { handleSignup(); } } }}
              style={{ width: '100%', background: theme.paper, border: `1px solid ${theme.line}`, padding: '12px 44px 12px 14px', fontSize: 15, color: theme.ink, outline: 'none', boxSizing: 'border-box' }} />
            <button onClick={() => setShowPassword(!showPassword)} type="button" style={{ position: 'absolute', top: '50%', right: 10, transform: 'translateY(-50%)', background: 'transparent', border: 'none', color: theme.inkFaint, cursor: 'pointer', padding: 4 }}>
              {showPassword ? <EyeOff size={15} /> : <Eye size={15} />}
            </button>
          </div>
        </div>

        {mode === 'signup' && (
          <div style={{ marginBottom: 12 }}>
            <label style={{ fontSize: 11, letterSpacing: '0.1em', textTransform: 'uppercase', color: theme.inkSoft, marginBottom: 6, display: 'block' }}>Confirm password</label>
            <input type={showPassword ? 'text' : 'password'} value={confirmPw} onChange={(e) => setConfirmPw(e.target.value)} autoComplete="new-password"
              onKeyDown={(e) => { if (e.key === 'Enter' && !busy) handleSignup(); }}
              style={{ width: '100%', background: theme.paper, border: `1px solid ${theme.line}`, padding: '12px 14px', fontSize: 15, color: theme.ink, outline: 'none', boxSizing: 'border-box' }} />
          </div>
        )}

        {error && (
          <div style={{ background: '#FDF5F3', border: `1px solid ${theme.danger}`, padding: '10px 12px', fontSize: 13, color: theme.danger, marginBottom: 12, display: 'flex', gap: 8, alignItems: 'flex-start' }}>
            <AlertTriangle size={14} style={{ flexShrink: 0, marginTop: 2 }} /> {error}
          </div>
        )}

        <button onClick={mode === 'login' ? handleLogin : handleSignup} disabled={busy}
          style={{ width: '100%', background: theme.ink, color: theme.paper, border: 'none', padding: '14px', fontSize: 14, cursor: busy ? 'wait' : 'pointer', opacity: busy ? 0.6 : 1, letterSpacing: '0.02em', marginBottom: 16 }}>
          {busy ? (mode === 'login' ? 'Logging in...' : 'Creating account...') : (mode === 'login' ? 'Log in' : 'Create account')}
        </button>

        <div style={{ textAlign: 'center', fontSize: 13, color: theme.inkSoft, marginBottom: 20 }}>
          {mode === 'login' ? (
            <>Don't have an account? <button onClick={() => { setMode('signup'); setError(''); }} style={{ background: 'none', border: 'none', color: theme.ink, textDecoration: 'underline', cursor: 'pointer', fontSize: 13, padding: 0 }}>Create one</button></>
          ) : (
            <>Already have an account? <button onClick={() => { setMode('login'); setError(''); }} style={{ background: 'none', border: 'none', color: theme.ink, textDecoration: 'underline', cursor: 'pointer', fontSize: 13, padding: 0 }}>Log in</button></>
          )}
        </div>

        <SecurityBadge />
      </div>
    </div>
  );
}

function SecurityBadge() {
  const [expanded, setExpanded] = useState(false);
  return (
    <div style={{ background: theme.paper, border: `1px solid ${theme.line}`, padding: '14px' }}>
      <button onClick={() => setExpanded(!expanded)}
        style={{ background: 'transparent', border: 'none', width: '100%', display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer', padding: 0, textAlign: 'left', color: theme.ink }}>
        <Shield size={16} color={theme.success} />
        <span style={{ fontSize: 12, fontWeight: 500, flex: 1 }}>How your data is protected</span>
        <ChevronRight size={14} color={theme.inkFaint} style={{ transform: expanded ? 'rotate(90deg)' : 'none', transition: 'transform 200ms' }} />
      </button>
      {expanded && (
        <div style={{ fontSize: 12, color: theme.inkSoft, lineHeight: 1.6, marginTop: 10, paddingTop: 10, borderTop: `1px solid ${theme.line}` }}>
          <div style={{ marginBottom: 6 }}>• Passwords hashed with PBKDF2 (210,000 iterations, SHA-256)</div>
          <div style={{ marginBottom: 6 }}>• Profile data encrypted with AES-GCM-256</div>
          <div style={{ marginBottom: 6 }}>• Encryption key derived from your password, never stored</div>
          <div>• All crypto runs locally in your browser (Web Crypto API)</div>
        </div>
      )}
    </div>
  );
}

// ============================================================================
// DATA NORMALISATION
// Ensures vault data always has the expected shape regardless of when it was
// saved. Prevents crashes from missing fields in older vault records.
// ============================================================================

function normaliseClientData(raw) {
  if (!raw || typeof raw !== 'object') return null;
  return {
    profile: raw.profile ? {
      name: raw.profile.name || '',
      goals: raw.profile.goals || [],
      axes: raw.profile.axes || { directness: 0.5, warmth: 0.5, structure: 0.5, energy: 0.5 },
      personaId: raw.profile.personaId || 'mentor',
      habits: Array.isArray(raw.profile.habits) ? raw.profile.habits : [],
      onboarded: raw.profile.onboarded || false,
    } : null,
    chat: Array.isArray(raw.chat) ? raw.chat : [],
    habitLog: raw.habitLog && typeof raw.habitLog === 'object' ? raw.habitLog : {},
    dailyReflections: raw.dailyReflections && typeof raw.dailyReflections === 'object' ? raw.dailyReflections : {},
    intentions: raw.intentions && typeof raw.intentions === 'object' ? raw.intentions : {},
    flowHistory: raw.flowHistory && typeof raw.flowHistory === 'object' ? raw.flowHistory : {},
    selectedCoach: raw.selectedCoach || null,
    briefPublished: raw.briefPublished || false,
    createdAt: raw.createdAt || new Date().toISOString(),
  };
}

function normaliseCoachData(raw) {
  if (!raw || typeof raw !== 'object') return null;
  return {
    coach: raw.coach ? {
      name: raw.coach.name || '',
      credential: raw.coach.credential || '',
      specialism: raw.coach.specialism || '',
      bio: raw.coach.bio || '',
      rate: raw.coach.rate || '0',
      axes: raw.coach.axes || { directness: 0.5, warmth: 0.5, structure: 0.5, energy: 0.5 },
      onboarded: raw.coach.onboarded || false,
      createdAt: raw.coach.createdAt || new Date().toISOString(),
    } : null,
  };
}

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }
  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }
  componentDidCatch(error, info) {
    console.error('[YourCoach] Uncaught error:', error, info);
  }
  render() {
    if (this.state.hasError) {
      return (
        <div style={{ minHeight: '100vh', background: '#F4EFE6', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '2rem', fontFamily: '"Fraunces", serif' }}>
          <div style={{ fontSize: 48, color: '#8B6F47', marginBottom: 24 }}>◈</div>
          <h2 style={{ fontWeight: 400, fontSize: '1.5rem', color: '#2A2420', marginBottom: 12, letterSpacing: '-0.02em' }}>Something went wrong</h2>
          <p style={{ fontSize: 14, color: '#5C544C', marginBottom: 24, textAlign: 'center', maxWidth: 360, lineHeight: 1.6 }}>
            An unexpected error occurred. Your data is safe — refresh the page to continue.
          </p>
          {process.env.NODE_ENV !== 'production' && this.state.error && (
            <pre style={{ background: '#2A2420', color: '#FBF8F2', padding: '12px', fontSize: 11, maxWidth: 500, overflow: 'auto', marginBottom: 16, borderRadius: 4 }}>
              {this.state.error.toString()}
            </pre>
          )}
          <button
            onClick={() => window.location.reload()}
            style={{ background: '#2A2420', color: '#FBF8F2', border: 'none', padding: '12px 24px', fontSize: 14, cursor: 'pointer' }}>
            Refresh page
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}

// ============================================================================
// PUSH NOTIFICATION SETUP
// ============================================================================

async function requestPushPermission() {
  if (!('Notification' in window) || !('serviceWorker' in navigator)) return;
  if (Notification.permission === 'denied') return;
  if (Notification.permission === 'granted') { await subscribeToPush(); return; }
  const permission = await Notification.requestPermission();
  if (permission === 'granted') await subscribeToPush();
}

async function subscribeToPush() {
  try {
    const reg = await navigator.serviceWorker.ready;
    const existing = await reg.pushManager.getSubscription();
    if (existing) { await notificationsApi.subscribe(existing.toJSON()).catch(() => {}); return; }
    const { publicKey } = await notificationsApi.getVapidKey();
    const subscription = await reg.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(publicKey),
    });
    await notificationsApi.subscribe(subscription.toJSON());
  } catch (e) {
    console.warn('[YourCoach] Push subscription failed:', e.message);
  }
}

function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - base64String.length % 4) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const rawData = window.atob(base64);
  const arr = new Uint8Array(rawData.length);
  for (let i = 0; i < rawData.length; ++i) arr[i] = rawData.charCodeAt(i);
  return arr;
}

// ============================================================================
// NOTIFICATION CENTRE
// ============================================================================

function NotificationCentre({ onClose, onCountChange }) {
  const [notifs, setNotifs] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    notificationsApi.getAll()
      .then(d => {
        setNotifs(d.notifications || []);
        const unread = (d.notifications || []).filter(n => !n.read).length;
        if (unread > 0) {
          notificationsApi.markRead([]).catch(() => {});
          onCountChange(0);
        }
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const typeIcon = (type) => ({
    new_message: <MessageSquare size={14} color={theme.accent} />,
    brief_accepted: <CheckCircle2 size={14} color={theme.success} />,
    coach_confirmed: <Users size={14} color={theme.success} />,
    check_in_reminder: <Sunrise size={14} color={theme.accent} />,
  }[type] || <Bell size={14} color={theme.inkFaint} />);

  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 200 }}>
      <div onClick={onClose} style={{ position: 'absolute', inset: 0, background: 'rgba(42,36,32,0.4)' }} />
      <div style={{ position: 'absolute', top: 0, right: 0, width: Math.min(360, window.innerWidth), height: '100vh', background: theme.bg, overflowY: 'auto', boxShadow: '-4px 0 24px rgba(0,0,0,0.15)' }}>
        <div style={{ padding: '20px', borderBottom: `1px solid ${theme.line}`, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div>
            <div style={{ fontSize: 10, letterSpacing: '0.25em', textTransform: 'uppercase', color: theme.inkFaint, marginBottom: 4 }}>Activity</div>
            <h2 style={{ fontFamily: '"Fraunces", serif', fontWeight: 400, fontSize: '1.5rem', color: theme.ink, margin: 0, letterSpacing: '-0.02em' }}>Notifications</h2>
          </div>
          <button onClick={onClose} style={{ background: 'transparent', border: 'none', color: theme.inkFaint, cursor: 'pointer', padding: 4 }}><X size={20} /></button>
        </div>
        <div style={{ padding: '12px' }}>
          {loading && <div style={{ textAlign: 'center', padding: '32px', color: theme.inkFaint, fontSize: 13 }}>Loading...</div>}
          {!loading && notifs.length === 0 && (
            <div style={{ textAlign: 'center', padding: '48px 16px' }}>
              <Bell size={32} color={theme.line} style={{ margin: '0 auto 12px', display: 'block' }} />
              <div style={{ fontFamily: '"Fraunces", serif', fontSize: 16, color: theme.inkSoft }}>All caught up</div>
              <div style={{ fontSize: 13, color: theme.inkFaint, marginTop: 4 }}>Notifications will appear here</div>
            </div>
          )}
          {notifs.map(n => (
            <div key={n.id} style={{ background: n.read ? 'transparent' : theme.paper, border: `1px solid ${n.read ? 'transparent' : theme.line}`, borderLeft: `3px solid ${n.read ? 'transparent' : theme.accent}`, padding: '14px', marginBottom: 8 }}>
              <div style={{ display: 'flex', gap: 10, alignItems: 'flex-start' }}>
                <div style={{ flexShrink: 0, marginTop: 2 }}>{typeIcon(n.type)}</div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 13, fontWeight: n.read ? 400 : 600, color: theme.ink, marginBottom: 3 }}>{n.title}</div>
                  <div style={{ fontSize: 12, color: theme.inkSoft, lineHeight: 1.4, marginBottom: 4 }}>{n.body}</div>
                  <div style={{ fontSize: 10, color: theme.inkFaint }}>{timeAgo(n.created_at)}</div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ============================================================================
// MAIN APP
// ============================================================================

function App() {
  const [session, setSession] = useState(null);
  const [tab, setTab] = useState('home');
  const [revealStep, setRevealStep] = useState(false);
  const [activeFlow, setActiveFlow] = useState(null);
  const [showOnboardingHelp, setShowOnboardingHelp] = useState(false);
  const [selectedClientEmail, setSelectedClientEmail] = useState(null);
  const [unreadCount, setUnreadCount] = useState(0);
  const [appLoading, setAppLoading] = useState(true);
  const [authMessage, setAuthMessage] = useState('');
  const [showNotifications, setShowNotifications] = useState(false);
  const [notifCount, setNotifCount] = useState(0); // shown on auth screen

  // On mount — try to restore session via refresh token cookie
  useEffect(() => {
    authApi.tryRefresh().then(ok => {
      if (!ok) {
        // No valid session — show login screen cleanly, no message needed
        setAppLoading(false);
        return;
      }
      // Refresh token valid but vault needs password to decrypt.
      // Show login with a helpful message rather than a blank screen.
      setAuthMessage('Your session is still active. Please log in to unlock your data.');
      setAppLoading(false);
    }).catch(() => {
      setAppLoading(false);
    });

    // Listen for forced logout events (token expired, reuse detected)
    const handleLogout = () => {
      setSession(null);
      setTab('home');
      setRevealStep(false);
      setActiveFlow(null);
      setAuthMessage('Your session has expired. Please log in again.');
    };
    window.addEventListener('auth:logout', handleLogout);
    return () => window.removeEventListener('auth:logout', handleLogout);
  }, []);

  // Poll unread count
  useEffect(() => {
    if (!session) return;
    const tick = async () => {
      try {
        const linkId = session.data?.selectedCoach?.linkId ||
                       session.data?.coach?.activeLinkId;
        if (linkId) {
          const msgs = await messagesApi.getThread(linkId);
          const unread = (msgs.messages || []).filter(m => m.to === session.id && !m.read).length;
          setUnreadCount(unread);
        }
      } catch {}
    };
    tick();
    const interval = setInterval(tick, 15000); // every 15s — polite to the server
    return () => clearInterval(interval);
  }, [session?.id, tab]);

  // Poll in-app notification count every 30s
  useEffect(() => {
    if (!session) return;
    const tick = () => {
      notificationsApi.getAll()
        .then(d => setNotifCount((d.notifications || []).filter(n => !n.read).length))
        .catch(() => {});
    };
    tick();
    const interval = setInterval(tick, 30000);
    return () => clearInterval(interval);
  }, [session?.id]);

  // Request push permission after login — politely, not immediately on load
  useEffect(() => {
    if (!session) return;
    // Delay 3 seconds so user has a moment to settle
    const timer = setTimeout(() => requestPushPermission(), 3000);
    return () => clearTimeout(timer);
  }, [session?.id]);

  // Save vault data to server (encrypted client-side first)
  const saveUserData = async (newData) => {
    if (!session?.vaultKey) return;
    try {
      const { encryptedData, iv } = await encryptVaultData(newData, session.vaultKey);
      await vaultApi.save({ encryptedData, iv, authTag: '' });

      // Sync client profile to server (needed for matching, briefs, signals)
      if (session.accountType === 'client' && newData.profile?.onboarded) {
        await coachesApi.saveClientProfile({
          name: newData.profile.name,
          goals: newData.profile.goals || [],
          axes: newData.profile.axes,
          personaId: newData.profile.personaId,
        }).catch(() => {});
      }

      // Push signals (Tier 2 — non-sensitive metrics) to server separately
      // Only after profile is onboarded and there's actual activity data
      if (session.accountType === 'client' &&
          newData.profile?.onboarded &&
          (newData.habitLog || newData.flowHistory)) {
        const today = new Date().toISOString().slice(0, 10);
        const todayHabits = newData.habitLog?.[today] || {};
        const habits = newData.profile?.habits || [];
        const habitsTotal = habits.length;
        const habitsDone = habits.filter(h => todayHabits[h.id]).length;
        // Only save signals if there's something meaningful
        if (habitsTotal > 0 || Object.keys(newData.flowHistory || {}).length > 0) {
          await signalsApi.save({
            date: today,
            morningDone: !!(newData.flowHistory?.[today] || []).find(f => f.flowId === 'morning'),
            eveningDone: !!(newData.flowHistory?.[today] || []).find(f => f.flowId === 'evening'),
            habitsTotal,
            habitsDone,
          }).catch(() => {});
        }
      }

      // If coach, sync profile to server
      if (session.accountType === 'coach' && newData.coach?.onboarded) {
        await coachesApi.saveProfile({
          name: newData.coach.name,
          credential: newData.coach.credential,
          specialism: newData.coach.specialism,
          bio: newData.coach.bio,
          rateGbp: parseInt(newData.coach.rate) || 0,
          axes: newData.coach.axes,
        }).catch(() => {});
      }
    } catch (err) {
      console.error('Save error:', err.message);
    }
    setSession(s => ({ ...s, data: newData }));
  };

  const updateData = (updater) => {
    const newData = typeof updater === 'function' ? updater(session.data) : updater;
    saveUserData(newData);
  };

  const logout = async () => {
    await authApi.logout().catch(() => {});
    sessionStorage.removeItem(SALT_SESSION_KEY);
    setSession(null);
    setTab('home');
    setActiveFlow(null);
    setSelectedClientEmail(null);
  };

  if (appLoading) {
    return (
      <><style>{FONTS}</style>
        <div style={{ minHeight: '100vh', background: theme.bg, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ fontFamily: '"Fraunces", serif', fontSize: 32, color: theme.accent }}>◈</div>
        </div>
      </>
    );
  }

  if (!session) {
    return (
      <><style>{FONTS}<PulseKeyframes /></style>
        <AuthScreen
          message={authMessage}
          onAuth={(s) => { setSession(s); setAuthMessage(''); setShowOnboardingHelp(false); }}
          onCreate={(s) => { setSession({ ...s, data: {} }); setAuthMessage(''); setShowOnboardingHelp(true); }}
        />
      </>
    );
  }

  // Coach flow
  if (session.accountType === 'coach') {
    if (!session.data?.coach?.onboarded) {
      return (<><style>{FONTS}<PulseKeyframes /></style>
        {showOnboardingHelp && <WelcomeHelp accountType="coach" onDismiss={() => setShowOnboardingHelp(false)} />}
        <Onboarding type="coach" onComplete={(coachProfile) => {
          updateData({ coach: { ...coachProfile, onboarded: true, createdAt: new Date().toISOString() } });
          setTab('home');
        }} />
      </>);
    }

    if (selectedClientEmail) {
      return (<><style>{FONTS}<PulseKeyframes /></style>
        <div style={{ minHeight: '100vh', background: theme.bg, paddingBottom: 76, fontFamily: 'Inter, sans-serif' }}>
          <CoachClientDetailView session={session} clientId={selectedClientEmail} onBack={() => setSelectedClientEmail(null)} />
        </div></>);
    }

    return (<><style>{FONTS}<PulseKeyframes /></style>
      <div style={{ minHeight: '100vh', background: theme.bg, paddingBottom: 76, fontFamily: 'Inter, sans-serif' }}>
        <button
          onClick={() => setShowNotifications(true)}
          style={{ position: 'fixed', top: 16, right: 16, zIndex: 50, background: theme.paper, border: `1px solid ${theme.line}`, borderRadius: '50%', width: 38, height: 38, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}>
          <Bell size={16} color={notifCount > 0 ? theme.accent : theme.inkFaint} />
          {notifCount > 0 && (
            <span style={{ position: 'absolute', top: -4, right: -4, background: theme.danger, color: theme.paper, fontSize: 9, fontWeight: 700, borderRadius: 10, minWidth: 16, height: 16, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '0 4px' }}>
              {notifCount > 9 ? '9+' : notifCount}
            </span>
          )}
        </button>
        {tab === 'home' && <CoachDashboard session={session} updateData={updateData} onOpenClient={setSelectedClientEmail} />}
        {tab === 'clients' && <CoachClientsView session={session} onOpenClient={setSelectedClientEmail} />}
        {tab === 'briefs' && <CoachBriefsView session={session} updateData={updateData} />}
        {tab === 'profile' && <CoachProfileView session={session} updateData={updateData} logout={logout} />}
        <CoachTabBar tab={tab} setTab={setTab} unread={unreadCount} />
        {showNotifications && (
          <NotificationCentre
            onClose={() => setShowNotifications(false)}
            onCountChange={setNotifCount}
          />
        )}
      </div></>);
  }

  // Client flow
  if (!session.data?.profile?.onboarded && !revealStep) {
    return (<><style>{FONTS}<PulseKeyframes /></style>
      {showOnboardingHelp && <WelcomeHelp accountType="client" onDismiss={() => setShowOnboardingHelp(false)} />}
      <Onboarding type="client" onComplete={(profile) => {
        updateData({ profile });
        setRevealStep(profile);
      }} />
    </>);
  }

  if (revealStep) {
    const profileForReveal = typeof revealStep === 'object' ? revealStep : session.data.profile;
    return (<><style>{FONTS}<PulseKeyframes /></style>
      <PersonaReveal profile={profileForReveal}
        onConfirm={(personaId) => {
          updateData({
            ...session.data,
            profile: { ...profileForReveal, personaId, onboarded: true },
            chat: [], habitLog: {}, dailyReflections: {}, intentions: {}, flowHistory: {},
            createdAt: new Date().toISOString(),
          });
          setRevealStep(false);
        }} />
    </>);
  }

  if (activeFlow) {
    return (<><style>{FONTS}<PulseKeyframes /></style>
      <GuidedFlow flow={FLOWS[activeFlow]} data={session.data} updateData={updateData} onClose={() => setActiveFlow(null)} />
    </>);
  }

  return (<><style>{FONTS}<PulseKeyframes /></style>
    <div style={{ minHeight: '100vh', background: theme.bg, paddingBottom: 76, fontFamily: 'Inter, sans-serif' }}>
      {/* Bell icon — fixed top right */}
      <button
        onClick={() => setShowNotifications(true)}
        style={{ position: 'fixed', top: 16, right: 16, zIndex: 50, background: theme.paper, border: `1px solid ${theme.line}`, borderRadius: '50%', width: 38, height: 38, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}>
        <Bell size={16} color={notifCount > 0 ? theme.accent : theme.inkFaint} />
        {notifCount > 0 && (
          <span style={{ position: 'absolute', top: -4, right: -4, background: theme.danger, color: theme.paper, fontSize: 9, fontWeight: 700, borderRadius: 10, minWidth: 16, height: 16, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '0 4px' }}>
            {notifCount > 9 ? '9+' : notifCount}
          </span>
        )}
      </button>

      {tab === 'home' && <HomeView session={session} updateData={updateData} setTab={setTab} onStartFlow={setActiveFlow} />}
      {tab === 'chat' && <ChatView session={session} updateData={updateData} onStartFlow={setActiveFlow} />}
      {tab === 'goals' && <GoalsView session={session} updateData={updateData} />}
      {tab === 'coaches' && <CoachesView session={session} updateData={updateData} />}
      {tab === 'settings' && <SettingsView session={session} updateData={updateData} logout={logout} />}
      <ClientTabBar tab={tab} setTab={setTab} personaAccent={PERSONAS[session.data.profile?.personaId]?.accent || theme.accent} unread={unreadCount} />

      {showNotifications && (
        <NotificationCentre
          onClose={() => setShowNotifications(false)}
          onCountChange={setNotifCount}
        />
      )}
    </div></>);
}

function PulseKeyframes() {
  return <>{`@keyframes pulse { 0%, 100% { opacity: 1; } 50% { opacity: 0.3; } } @keyframes slideUp { from { transform: translateY(100%); } to { transform: translateY(0); } }`}</>;
}

function formatCredential(id) {
  const map = { 'icf-acc': 'ICF ACC', 'icf-pcc': 'ICF PCC', 'icf-mcc': 'ICF MCC', 'emcc-practitioner': 'EMCC Practitioner', 'emcc-senior': 'EMCC Senior Practitioner', 'ac': 'Association for Coaching', 'other': 'Accredited Coach' };
  return map[id] || id;
}

// ============================================================================
// WELCOME HELP — shown after signup
// ============================================================================

function WelcomeHelp({ accountType, onDismiss }) {
  const [step, setStep] = useState(0);

  const clientSteps = [
    { title: 'Welcome to YourCoach', body: 'Let\'s get you set up. The next few minutes will create your coaching profile — we use this to match you to an AI persona and human coaches who fit how you work.', icon: <Sparkles size={28} /> },
    { title: 'Your profile is yours alone', body: 'Everything you share in check-ins, chats and reflections is encrypted on your device. Even human coaches only see engagement signals, not what you wrote.', icon: <Shield size={28} /> },
    { title: 'Four guided check-ins', body: 'Morning (set your day), Evening (reflect on it), I\'m stuck (work through a problem), Weekly review (zoom out). They use real coaching frameworks, not blank pages.', icon: <ClipboardCheck size={28} /> },
    { title: 'AI daily, human fortnightly', body: 'Your AI coach is always available. Your human coach meets with you every two weeks for real accountability, with context handed over by the app.', icon: <Users size={28} /> },
  ];

  const coachSteps = [
    { title: 'Welcome, coach', body: 'Let\'s build your profile. We\'ll capture your credentials, specialism, and style — the same four axes your clients answer — so we can match you well.', icon: <Sparkles size={28} /> },
    { title: 'You see signals, not substance', body: 'When clients complete check-ins, you see patterns — engagement consistency, recurring themes, what they\'ve flagged to discuss. You only see raw content they explicitly share with you.', icon: <EyeOff size={28} /> },
    { title: 'Client briefs in your inbox', body: 'If a client signs up without a strong match, their anonymised brief goes into your Briefs tab. You can opt in if it feels like a fit for your practice.', icon: <Inbox size={28} /> },
    { title: 'Pre-session context', body: 'Before each session you get an AI-generated brief — the week\'s engagement, themes, what the client wants to discuss. Session time stays in coaching, not catching up.', icon: <BookOpen size={28} /> },
  ];

  const steps = accountType === 'coach' ? coachSteps : clientSteps;
  const current = steps[step];
  const isLast = step === steps.length - 1;

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(42, 36, 32, 0.6)', zIndex: 100, display: 'flex', alignItems: 'flex-end', animation: 'slideUp 300ms ease' }}>
      <style>{FONTS}</style>
      <div style={{ width: '100%', background: theme.bg, borderTopLeftRadius: 24, borderTopRightRadius: 24, padding: '32px 24px 24px', maxHeight: '85vh', overflowY: 'auto' }}>
        <div style={{ maxWidth: 440, margin: '0 auto' }}>
          <button onClick={onDismiss} style={{ background: 'transparent', border: 'none', color: theme.inkFaint, cursor: 'pointer', padding: 0, fontSize: 13, marginBottom: 24, display: 'flex', alignItems: 'center', gap: 6 }}>
            Skip intro <X size={14} />
          </button>

          <div style={{ display: 'flex', gap: 6, marginBottom: 32 }}>
            {steps.map((_, i) => (
              <div key={i} style={{ flex: 1, height: 3, background: i <= step ? theme.accent : theme.line, transition: 'background 300ms' }} />
            ))}
          </div>

          <div style={{ color: theme.accent, marginBottom: 20 }}>{current.icon}</div>

          <h2 style={{ fontFamily: '"Fraunces", serif', fontSize: '1.75rem', color: theme.ink, margin: 0, marginBottom: 16, letterSpacing: '-0.02em', lineHeight: 1.2, fontWeight: 400 }}>
            {current.title}
          </h2>
          <p style={{ fontFamily: '"Fraunces", serif', fontSize: 17, color: theme.inkSoft, lineHeight: 1.5, margin: 0, marginBottom: 32 }}>
            {current.body}
          </p>

          <div style={{ display: 'flex', gap: 10 }}>
            {step > 0 && (
              <button onClick={() => setStep(step - 1)} style={{ background: 'transparent', border: `1px solid ${theme.line}`, padding: '12px 16px', fontSize: 14, color: theme.inkSoft, cursor: 'pointer' }}>Back</button>
            )}
            <button onClick={() => isLast ? onDismiss() : setStep(step + 1)}
              style={{ flex: 1, background: theme.ink, color: theme.paper, border: 'none', padding: '12px 16px', fontSize: 14, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}>
              {isLast ? 'Let\'s get started' : 'Next'} <ArrowRight size={14} />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ============================================================================
// ONBOARDING
// ============================================================================

const CLIENT_ONBOARDING = [
  { key: 'name', prompt: 'First, what should your coach call you?', type: 'text', placeholder: 'Your name or preferred alias',
    help: 'This is what your AI coach and any human coach you work with will call you. Use whatever feels right — first name, full name, or an alias.' },
  { key: 'goals', prompt: 'What areas of your life do you want to work on?', subprompt: 'Pick as many as feel right. You can change these any time.', type: 'multi',
    options: [
      { id: 'health', label: 'Physical health & energy' }, { id: 'mind', label: 'Mental wellbeing & balance' },
      { id: 'work', label: 'Career & work' }, { id: 'focus', label: 'Focus & productivity' },
      { id: 'relationships', label: 'Relationships' }, { id: 'money', label: 'Money & finances' },
      { id: 'purpose', label: 'Purpose & direction' }, { id: 'habits', label: 'Daily habits & discipline' },
    ],
    help: 'These tell your AI coach where to focus conversations, and help us match you to human coaches whose specialism fits.' },
  { key: 'directness', prompt: 'When you need feedback, how do you want it delivered?', type: 'scale', leftLabel: 'Gently, with care', rightLabel: 'Straight, no sugar-coating', axis: 'directness',
    help: 'This is one of four style axes. We use them to match you to an AI persona and to human coaches whose style fits.' },
  { key: 'warmth', prompt: 'What kind of energy do you want from your coach?', type: 'scale', leftLabel: 'Challenging & focused', rightLabel: 'Warm & supportive', axis: 'warmth' },
  { key: 'structure', prompt: 'How do you work best?', type: 'scale', leftLabel: 'Loose & flexible', rightLabel: 'Structured & planned', axis: 'structure' },
  { key: 'energy', prompt: 'What pace suits you?', type: 'scale', leftLabel: 'Calm & reflective', rightLabel: 'High-energy & driving', axis: 'energy' },
  { key: 'habits', prompt: 'Let\'s set up a few habits to start tracking.', subprompt: 'Pick from suggestions or add your own. You can edit any time.', type: 'habits',
    help: 'Small, specific habits work best. "Read 20 minutes" beats "read more". Two to five habits is a good starting place.' },
];

const COACH_ONBOARDING = [
  { key: 'name', prompt: 'Your full name?', subprompt: 'As it will appear to potential clients.', type: 'text', placeholder: 'Jane Smith' },
  { key: 'credential', prompt: 'Your primary coaching credential?', type: 'choice',
    options: [
      { id: 'icf-acc', label: 'ICF Associate (ACC)' }, { id: 'icf-pcc', label: 'ICF Professional (PCC)' },
      { id: 'icf-mcc', label: 'ICF Master (MCC)' }, { id: 'emcc-practitioner', label: 'EMCC Practitioner' },
      { id: 'emcc-senior', label: 'EMCC Senior Practitioner' }, { id: 'ac', label: 'Association for Coaching' },
      { id: 'other', label: 'Other accredited' },
    ],
    help: 'Verification documents will be requested after sign-up. The MVP is limited to practitioners with recognised credentials.' },
  { key: 'specialism', prompt: 'What do you specialise in?', subprompt: 'A short phrase clients will see.', type: 'text', placeholder: 'e.g. Career transitions, executive coaching' },
  { key: 'bio', prompt: 'A short bio for your profile.', subprompt: 'Two or three sentences. What makes your practice yours?', type: 'text', placeholder: 'Tell clients who you are...', rows: 4 },
  { key: 'rate', prompt: 'Your session rate (£)?', subprompt: 'Per 60-minute session. You can adjust this later.', type: 'number', placeholder: '85' },
  { key: 'directness', prompt: 'How direct is your coaching style?', subprompt: 'These four questions are identical to what clients answer — we use them to match you.', type: 'scale', leftLabel: 'Gentle, reflective', rightLabel: 'Direct, challenging', axis: 'directness',
    help: 'Be honest. A client who wants gentle support with a direct coach won\'t work, no matter how brilliant either of you is. Matching well beats matching widely.' },
  { key: 'warmth', prompt: 'How warm is your presence?', type: 'scale', leftLabel: 'Focused, analytical', rightLabel: 'Warm, nurturing', axis: 'warmth' },
  { key: 'structure', prompt: 'How structured are your sessions?', type: 'scale', leftLabel: 'Emergent, flexible', rightLabel: 'Structured, planned', axis: 'structure' },
  { key: 'energy', prompt: 'What pace do you work at?', type: 'scale', leftLabel: 'Measured, calm', rightLabel: 'Energising, driving', axis: 'energy' },
];

function Onboarding({ type, onComplete }) {
  const steps = type === 'coach' ? COACH_ONBOARDING : CLIENT_ONBOARDING;
  const [stepIdx, setStepIdx] = useState(0);
  const [data, setData] = useState({
    name: '', goals: [], directness: 0.5, warmth: 0.5, structure: 0.5, energy: 0.5, habits: [],
    credential: '', specialism: '', bio: '', rate: '',
  });
  const step = steps[stepIdx];
  const isLast = stepIdx === steps.length - 1;

  const canAdvance = () => {
    if (step.type === 'text' && !step.rows) return data[step.key]?.trim().length > 0;
    if (step.type === 'text' && step.rows) return data[step.key]?.trim().length > 0;
    if (step.type === 'number') return data[step.key]?.toString().length > 0;
    if (step.type === 'multi') return data[step.key]?.length > 0;
    if (step.type === 'choice') return !!data[step.key];
    return true;
  };

  const advance = () => {
    if (isLast) {
      const axes = { directness: data.directness, warmth: data.warmth, structure: data.structure, energy: data.energy };
      if (type === 'coach') onComplete({ ...data, axes });
      else {
        const persona = recommendPersona(axes);
        onComplete({ ...data, axes, personaId: persona.id });
      }
    } else setStepIdx(stepIdx + 1);
  };

  return (
    <div style={{ minHeight: '100vh', background: theme.bg, display: 'flex', flexDirection: 'column', padding: '2rem 1.5rem' }}>
      <div style={{ display: 'flex', gap: 6, marginBottom: '2rem' }}>
        {steps.map((_, i) => (<div key={i} style={{ flex: 1, height: 2, background: i <= stepIdx ? theme.ink : theme.line, transition: 'background 400ms' }} />))}
      </div>

      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center', maxWidth: 520, width: '100%', margin: '0 auto' }}>
        <div style={{ fontSize: 11, letterSpacing: '0.2em', textTransform: 'uppercase', color: theme.inkFaint, marginBottom: '1.5rem' }}>
          {type === 'coach' ? 'Coach sign-up · ' : ''}Step {stepIdx + 1} of {steps.length}
        </div>
        <h2 style={{ fontFamily: '"Fraunces", serif', fontWeight: 400, fontSize: 'clamp(1.75rem, 5vw, 2.5rem)', lineHeight: 1.15, color: theme.ink, margin: 0, marginBottom: step.subprompt ? '0.75rem' : '1.5rem', letterSpacing: '-0.02em' }}>
          {step.prompt}
        </h2>
        {step.subprompt && <p style={{ fontSize: 15, color: theme.inkSoft, marginBottom: '1.5rem' }}>{step.subprompt}</p>}
        {step.help && (
          <InfoCallout icon={<Info size={14} />} tone="info">
            {step.help}
          </InfoCallout>
        )}

        <div style={{ marginTop: 16 }}>
          {step.type === 'text' && !step.rows && (
            <input type="text" value={data[step.key]} onChange={(e) => setData({ ...data, [step.key]: e.target.value })} placeholder={step.placeholder} autoFocus
              style={{ fontFamily: '"Fraunces", serif', fontSize: 22, background: 'transparent', border: 'none', borderBottom: `1px solid ${theme.line}`, padding: '0.75rem 0', color: theme.ink, outline: 'none', width: '100%' }} />
          )}
          {step.type === 'text' && step.rows && (
            <DictableText value={data[step.key]} onChange={(v) => setData({ ...data, [step.key]: v })} placeholder={step.placeholder} rows={step.rows} autoFocus />
          )}
          {step.type === 'number' && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <span style={{ fontFamily: '"Fraunces", serif', fontSize: 28, color: theme.ink }}>£</span>
              <input type="number" value={data[step.key]} onChange={(e) => setData({ ...data, [step.key]: e.target.value })} placeholder={step.placeholder} autoFocus
                style={{ fontFamily: '"Fraunces", serif', fontSize: 28, background: 'transparent', border: 'none', borderBottom: `1px solid ${theme.line}`, padding: '0.75rem 0', color: theme.ink, outline: 'none', width: '100%' }} />
            </div>
          )}
          {step.type === 'multi' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {step.options.map(opt => {
                const selected = data[step.key].includes(opt.id);
                return (
                  <button key={opt.id}
                    onClick={() => {
                      const c = data[step.key];
                      setData({ ...data, [step.key]: selected ? c.filter(x => x !== opt.id) : [...c, opt.id] });
                    }}
                    style={{ display: 'flex', alignItems: 'center', gap: 12, background: selected ? theme.ink : theme.paper, color: selected ? theme.paper : theme.ink, border: `1px solid ${selected ? theme.ink : theme.line}`, padding: '14px 16px', fontSize: 15, textAlign: 'left', cursor: 'pointer', transition: 'all 200ms' }}>
                    <div style={{ width: 18, height: 18, border: `1px solid ${selected ? theme.paper : theme.inkFaint}`, display: 'flex', alignItems: 'center', justifyContent: 'center', background: selected ? theme.paper : 'transparent' }}>
                      {selected && <Check size={12} color={theme.ink} strokeWidth={3} />}
                    </div>
                    {opt.label}
                  </button>
                );
              })}
            </div>
          )}
          {step.type === 'choice' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {step.options.map(opt => {
                const selected = data[step.key] === opt.id;
                return (
                  <button key={opt.id} onClick={() => setData({ ...data, [step.key]: opt.id })}
                    style={{ display: 'flex', alignItems: 'center', gap: 12, background: selected ? theme.ink : theme.paper, color: selected ? theme.paper : theme.ink, border: `1px solid ${selected ? theme.ink : theme.line}`, padding: '14px 16px', fontSize: 15, textAlign: 'left', cursor: 'pointer', transition: 'all 200ms' }}>
                    <div style={{ width: 16, height: 16, borderRadius: '50%', border: `1px solid ${selected ? theme.paper : theme.inkFaint}`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      {selected && <div style={{ width: 8, height: 8, borderRadius: '50%', background: theme.paper }} />}
                    </div>
                    {opt.label}
                  </button>
                );
              })}
            </div>
          )}
          {step.type === 'scale' && <ScaleInput value={data[step.axis]} onChange={(v) => setData({ ...data, [step.axis]: v })} leftLabel={step.leftLabel} rightLabel={step.rightLabel} />}
          {step.type === 'habits' && <HabitsSetup habits={data.habits} onChange={(h) => setData({ ...data, habits: h })} />}
        </div>
      </div>

      <div style={{ display: 'flex', gap: 12, maxWidth: 520, width: '100%', margin: '2rem auto 0' }}>
        {stepIdx > 0 && (<button onClick={() => setStepIdx(stepIdx - 1)} style={{ background: 'transparent', border: `1px solid ${theme.line}`, padding: '14px 20px', fontSize: 14, color: theme.inkSoft, cursor: 'pointer' }}>Back</button>)}
        <button onClick={advance} disabled={!canAdvance()}
          style={{ flex: 1, background: canAdvance() ? theme.ink : theme.line, color: theme.paper, border: 'none', padding: '14px 20px', fontSize: 14, cursor: canAdvance() ? 'pointer' : 'not-allowed', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}>
          {isLast ? (type === 'coach' ? 'Go to dashboard' : 'Meet your coach') : 'Continue'}<ArrowRight size={16} />
        </button>
      </div>
    </div>
  );
}

function ScaleInput({ value, onChange, leftLabel, rightLabel }) {
  return (
    <div>
      <input type="range" min={0} max={100} value={value * 100} onChange={(e) => onChange(parseInt(e.target.value) / 100)} style={{ width: '100%', accentColor: theme.ink, marginBottom: '0.75rem' }} />
      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, color: theme.inkSoft }}>
        <span>{leftLabel}</span><span>{rightLabel}</span>
      </div>
    </div>
  );
}

function HabitsSetup({ habits, onChange }) {
  const [draft, setDraft] = useState('');
  const suggestions = ['Morning movement', 'Read 20 minutes', 'No phone before bed', 'Drink 2L water', 'Journal 5 minutes', 'Meditate 10 min', '10,000 steps', 'Sleep by 11pm', 'Stretch', 'Weekly planning', 'No alcohol weekdays', 'Cold shower'];
  const isAdded = (s) => habits.some(h => h.name === s);
  const addHabit = (name) => {
    if (!name.trim() || isAdded(name)) return;
    onChange([...habits, { id: Date.now().toString() + Math.random(), name: name.trim() }]);
  };

  return (
    <div>
      <div style={{ display: 'flex', gap: 8, marginBottom: 20 }}>
        <input value={draft} onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter' && draft.trim()) { addHabit(draft); setDraft(''); } }}
          placeholder="Add your own habit..."
          style={{ flex: 1, fontSize: 15, background: theme.paper, border: `1px solid ${theme.line}`, padding: '12px 14px', color: theme.ink, outline: 'none' }} />
        <button onClick={() => { if (draft.trim()) { addHabit(draft); setDraft(''); } }} style={{ background: theme.ink, color: theme.paper, border: 'none', padding: '0 16px', cursor: 'pointer' }}><Plus size={16} /></button>
      </div>
      <div style={{ marginBottom: habits.length > 0 ? 20 : 0 }}>
        <div style={{ fontSize: 12, letterSpacing: '0.1em', textTransform: 'uppercase', color: theme.inkFaint, marginBottom: 10 }}>Tap to add</div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
          {suggestions.map(s => {
            const added = isAdded(s);
            return (
              <button key={s} onClick={() => addHabit(s)} disabled={added}
                style={{ background: added ? theme.bgDeep : 'transparent', border: `1px solid ${added ? theme.accent : theme.line}`, padding: '6px 12px', fontSize: 13, color: added ? theme.accent : theme.inkSoft, cursor: added ? 'default' : 'pointer', borderRadius: 999, opacity: added ? 0.6 : 1 }}>
                {added ? '✓' : '+'} {s}
              </button>
            );
          })}
        </div>
      </div>
      {habits.length > 0 && (
        <div>
          <div style={{ fontSize: 12, letterSpacing: '0.1em', textTransform: 'uppercase', color: theme.inkFaint, marginBottom: 10 }}>Your habits ({habits.length})</div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {habits.map(h => (
              <div key={h.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: theme.paper, padding: '10px 14px', borderLeft: `2px solid ${theme.accent}`, fontSize: 14, color: theme.ink }}>
                {h.name}
                <button onClick={() => onChange(habits.filter(x => x.id !== h.id))} style={{ background: 'none', border: 'none', color: theme.inkFaint, cursor: 'pointer', fontSize: 18 }}>×</button>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ============================================================================
// PERSONA REVEAL
// ============================================================================

function PersonaReveal({ profile, onConfirm }) {
  const matched = PERSONAS[profile.personaId];
  const [showAll, setShowAll] = useState(false);
  const [selected, setSelected] = useState(profile.personaId);

  if (!showAll) {
    return (
      <div style={{ minHeight: '100vh', background: theme.bg, display: 'flex', flexDirection: 'column', padding: '2rem 1.5rem' }}>
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center', maxWidth: 520, width: '100%', margin: '0 auto', textAlign: 'center' }}>
          <div style={{ fontSize: 11, letterSpacing: '0.25em', textTransform: 'uppercase', color: theme.inkFaint, marginBottom: '2rem' }}>Your matched coach</div>
          <div style={{ fontSize: 72, color: matched.accent, fontFamily: '"Fraunces", serif', marginBottom: '1rem', lineHeight: 1 }}>{matched.glyph}</div>
          <h1 style={{ fontFamily: '"Fraunces", serif', fontWeight: 400, fontSize: 'clamp(2rem, 7vw, 3rem)', color: theme.ink, margin: 0, marginBottom: '0.5rem', letterSpacing: '-0.02em' }}>{matched.name}</h1>
          <div style={{ fontFamily: '"Fraunces", serif', fontStyle: 'italic', fontSize: 18, color: theme.inkSoft, marginBottom: '2rem' }}>{matched.tagline}</div>
          <p style={{ fontSize: 16, lineHeight: 1.6, color: theme.ink, marginBottom: '3rem' }}>{matched.description}</p>
          <button onClick={() => onConfirm(matched.id)} style={{ background: theme.ink, color: theme.paper, border: 'none', padding: '16px 20px', fontSize: 14, cursor: 'pointer', marginBottom: 12 }}>Start with {matched.name}</button>
          <button onClick={() => setShowAll(true)} style={{ background: 'transparent', border: 'none', color: theme.inkSoft, fontSize: 14, cursor: 'pointer', textDecoration: 'underline', textUnderlineOffset: 4 }}>See all personas</button>
        </div>
      </div>
    );
  }

  return (
    <div style={{ minHeight: '100vh', background: theme.bg, padding: '2rem 1.5rem' }}>
      <div style={{ maxWidth: 520, margin: '0 auto' }}>
        <div style={{ fontSize: 11, letterSpacing: '0.25em', textTransform: 'uppercase', color: theme.inkFaint, marginBottom: '1rem' }}>Choose your coach</div>
        <h2 style={{ fontFamily: '"Fraunces", serif', fontWeight: 400, fontSize: '2rem', color: theme.ink, margin: 0, marginBottom: '1.5rem', letterSpacing: '-0.02em' }}>Pick who guides you</h2>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12, marginBottom: 24 }}>
          {Object.values(PERSONAS).map(p => {
            const isMatch = p.id === profile.personaId;
            const isSel = selected === p.id;
            return (
              <button key={p.id} onClick={() => setSelected(p.id)}
                style={{ background: isSel ? theme.paper : 'transparent', border: `1px solid ${isSel ? theme.ink : theme.line}`, padding: '16px', cursor: 'pointer', textAlign: 'left', display: 'flex', gap: 16, alignItems: 'flex-start', transition: 'all 200ms' }}>
                <div style={{ fontSize: 32, color: p.accent, fontFamily: '"Fraunces", serif', lineHeight: 1 }}>{p.glyph}</div>
                <div style={{ flex: 1 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                    <span style={{ fontFamily: '"Fraunces", serif', fontSize: 18, fontWeight: 500, color: theme.ink }}>{p.name}</span>
                    {isMatch && <span style={{ fontSize: 9, letterSpacing: '0.15em', textTransform: 'uppercase', color: theme.accent, border: `1px solid ${theme.accent}`, padding: '2px 6px' }}>Match</span>}
                  </div>
                  <div style={{ fontFamily: '"Fraunces", serif', fontStyle: 'italic', fontSize: 13, color: theme.inkSoft, marginBottom: 6 }}>{p.tagline}</div>
                  <div style={{ fontSize: 13, color: theme.inkSoft, lineHeight: 1.5 }}>{p.description}</div>
                </div>
              </button>
            );
          })}
        </div>
        <button onClick={() => onConfirm(selected)} style={{ width: '100%', background: theme.ink, color: theme.paper, border: 'none', padding: '16px 20px', fontSize: 14, cursor: 'pointer' }}>Start with {PERSONAS[selected].name}</button>
      </div>
    </div>
  );
}

// ============================================================================
// GUIDED FLOW
// ============================================================================

function GuidedFlow({ flow, data, updateData, onClose }) {
  const persona = PERSONAS[data.profile?.personaId] || PERSONAS.mentor;
  const [stepIdx, setStepIdx] = useState(-1);
  const [answers, setAnswers] = useState({});
  const [closing, setClosing] = useState(false);
  const [coachResponse, setCoachResponse] = useState('');
  const [loadingCoach, setLoadingCoach] = useState(false);

  const steps = flow.steps;
  const isIntro = stepIdx === -1;
  const isLastStep = stepIdx === steps.length - 1;
  const step = isIntro ? null : steps[stepIdx];

  const canAdvance = () => {
    if (isIntro) return true;
    if (step.optional) return true;
    const val = answers[step.key];
    if (step.type === 'text') return val && val.trim().length > 0;
    if (step.type === 'choice') return !!val;
    if (step.type === 'scale5') return val !== undefined;
    return true;
  };

  const advance = async () => {
    if (isIntro) { setStepIdx(0); return; }
    if (isLastStep) {
      setClosing(true); setLoadingCoach(true);
      const today = new Date().toISOString().slice(0, 10);
      const entry = { flowId: flow.id, answers, completedAt: new Date().toISOString() };
      const dayHistory = (data.flowHistory || {})[today] || [];
      const newData = { ...data, flowHistory: { ...data.flowHistory, [today]: [...dayHistory, entry] } };
      if (flow.id === 'morning' && answers.oneThing) newData.intentions = { ...data.intentions, [today]: answers.oneThing };
      if (flow.id === 'evening' && answers.mood !== undefined) newData.dailyReflections = { ...data.dailyReflections, [today]: { mood: answers.mood, note: answers.went_well || '', ts: Date.now() } };
      updateData(newData);

      try {
        const result = await aiApi.flowClosing({
          personaId: persona.id,
          flowId: flow.id,
          prompt: flow.coachClosingPrompt(answers),
        });
        setCoachResponse(result.text || 'Well done for showing up today.');
      } catch (e) { setCoachResponse('Well done for showing up today.'); }
      finally { setLoadingCoach(false); }
    } else setStepIdx(stepIdx + 1);
  };

  if (closing) {
    return (
      <div style={{ minHeight: '100vh', background: theme.bg, padding: '2rem 1.5rem', display: 'flex', flexDirection: 'column' }}>
        <div style={{ maxWidth: 520, margin: '0 auto', width: '100%', flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
          <div style={{ textAlign: 'center', marginBottom: '2rem' }}>
            <div style={{ fontSize: 56, color: persona.accent, fontFamily: '"Fraunces", serif', lineHeight: 1, marginBottom: 16 }}>{persona.glyph}</div>
            <div style={{ fontSize: 11, letterSpacing: '0.25em', textTransform: 'uppercase', color: theme.inkFaint, marginBottom: 8 }}>{persona.name} says</div>
          </div>
          <div style={{ background: theme.paper, border: `1px solid ${theme.line}`, borderLeft: `3px solid ${persona.accent}`, padding: '20px 22px', marginBottom: '2rem', minHeight: 100 }}>
            {loadingCoach ? (
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, color: theme.inkFaint, fontSize: 14, fontStyle: 'italic' }}><Sparkles size={14} /> Reflecting on what you shared...</div>
            ) : (
              <div style={{ fontFamily: '"Fraunces", serif', fontSize: 17, lineHeight: 1.6, color: theme.ink, whiteSpace: 'pre-wrap' }}>{coachResponse}</div>
            )}
          </div>
          <button onClick={onClose} style={{ background: theme.ink, color: theme.paper, border: 'none', padding: '14px 20px', fontSize: 14, cursor: 'pointer', width: '100%' }}>Done</button>
        </div>
      </div>
    );
  }

  if (isIntro) {
    const Icon = flow.icon;
    return (
      <div style={{ minHeight: '100vh', background: theme.bg, padding: '2rem 1.5rem', display: 'flex', flexDirection: 'column' }}>
        <button onClick={onClose} style={{ alignSelf: 'flex-start', background: 'transparent', border: 'none', color: theme.inkSoft, fontSize: 14, cursor: 'pointer' }}>× Close</button>
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center', maxWidth: 520, width: '100%', margin: '0 auto', textAlign: 'center' }}>
          <div style={{ display: 'inline-flex', alignSelf: 'center', width: 56, height: 56, background: theme.paper, border: `1px solid ${theme.line}`, borderRadius: '50%', alignItems: 'center', justifyContent: 'center', marginBottom: 20, color: persona.accent }}><Icon size={24} /></div>
          <div style={{ fontSize: 11, letterSpacing: '0.25em', textTransform: 'uppercase', color: theme.inkFaint, marginBottom: 8 }}>Guided by {persona.name}</div>
          <h1 style={{ fontFamily: '"Fraunces", serif', fontWeight: 400, fontSize: '2.25rem', color: theme.ink, margin: 0, marginBottom: 12, letterSpacing: '-0.02em' }}>{flow.title}</h1>
          <p style={{ fontFamily: '"Fraunces", serif', fontStyle: 'italic', fontSize: 18, color: theme.inkSoft, marginBottom: 8 }}>{flow.subtitle}</p>
          <p style={{ fontSize: 13, color: theme.inkFaint, marginBottom: '3rem' }}>About {flow.estMinutes} minutes · {flow.steps.length} prompts · dictation available</p>
          <button onClick={advance} style={{ background: theme.ink, color: theme.paper, border: 'none', padding: '14px 20px', fontSize: 14, cursor: 'pointer', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}>Begin <ArrowRight size={16} /></button>
        </div>
      </div>
    );
  }

  return (
    <div style={{ minHeight: '100vh', background: theme.bg, display: 'flex', flexDirection: 'column', padding: '2rem 1.5rem' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: '2rem' }}>
        <button onClick={onClose} style={{ background: 'transparent', border: 'none', color: theme.inkSoft, fontSize: 14, cursor: 'pointer', padding: 0 }}>× Close</button>
        <div style={{ flex: 1, display: 'flex', gap: 4 }}>
          {steps.map((_, i) => (<div key={i} style={{ flex: 1, height: 2, background: i <= stepIdx ? persona.accent : theme.line, transition: 'background 300ms' }} />))}
        </div>
        <div style={{ fontSize: 11, color: theme.inkFaint }}>{stepIdx + 1}/{steps.length}</div>
      </div>

      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center', maxWidth: 520, width: '100%', margin: '0 auto' }}>
        <div style={{ fontSize: 11, letterSpacing: '0.25em', textTransform: 'uppercase', color: persona.accent, marginBottom: '1.5rem' }}>{flow.title}</div>
        <h2 style={{ fontFamily: '"Fraunces", serif', fontWeight: 400, fontSize: 'clamp(1.5rem, 4.5vw, 2.25rem)', lineHeight: 1.2, color: theme.ink, margin: 0, marginBottom: step.subprompt ? 10 : 32, letterSpacing: '-0.02em' }}>{step.prompt}</h2>
        {step.subprompt && <p style={{ fontFamily: '"Fraunces", serif', fontStyle: 'italic', fontSize: 15, color: theme.inkSoft, marginBottom: 32 }}>{step.subprompt}</p>}

        {step.type === 'text' && <DictableText value={answers[step.key] || ''} onChange={(v) => setAnswers({ ...answers, [step.key]: v })} placeholder={step.placeholder} autoFocus rows={3} />}
        {step.type === 'choice' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {step.options.map(opt => {
              const selected = answers[step.key] === opt.id;
              return (
                <button key={opt.id} onClick={() => setAnswers({ ...answers, [step.key]: opt.id })}
                  style={{ display: 'flex', alignItems: 'center', gap: 12, background: selected ? theme.ink : theme.paper, color: selected ? theme.paper : theme.ink, border: `1px solid ${selected ? theme.ink : theme.line}`, padding: '14px 16px', fontSize: 15, textAlign: 'left', cursor: 'pointer' }}>
                  <div style={{ width: 16, height: 16, borderRadius: '50%', border: `1px solid ${selected ? theme.paper : theme.inkFaint}`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    {selected && <div style={{ width: 8, height: 8, borderRadius: '50%', background: theme.paper }} />}
                  </div>
                  {opt.label}
                </button>
              );
            })}
          </div>
        )}
        {step.type === 'scale5' && (
          <div style={{ display: 'flex', gap: 6 }}>
            {[{ v: -2, l: 'Rough' }, { v: -1, l: 'Meh' }, { v: 0, l: 'OK' }, { v: 1, l: 'Good' }, { v: 2, l: 'Great' }].map(m => {
              const selected = answers[step.key] === m.v;
              return (
                <button key={m.v} onClick={() => setAnswers({ ...answers, [step.key]: m.v })}
                  style={{ flex: 1, background: selected ? theme.ink : theme.paper, color: selected ? theme.paper : theme.ink, border: `1px solid ${selected ? theme.ink : theme.line}`, padding: '18px 4px', fontSize: 13, cursor: 'pointer' }}>{m.l}</button>
              );
            })}
          </div>
        )}
      </div>

      <div style={{ display: 'flex', gap: 12, maxWidth: 520, width: '100%', margin: '2rem auto 0' }}>
        {stepIdx > 0 && (<button onClick={() => setStepIdx(stepIdx - 1)} style={{ background: 'transparent', border: `1px solid ${theme.line}`, padding: '14px 20px', fontSize: 14, color: theme.inkSoft, cursor: 'pointer' }}>Back</button>)}
        {step.optional && (!answers[step.key] || !answers[step.key].toString().trim()) && (
          <button onClick={advance} style={{ background: 'transparent', border: `1px solid ${theme.line}`, padding: '14px 20px', fontSize: 14, color: theme.inkSoft, cursor: 'pointer' }}>Skip</button>
        )}
        <button onClick={advance} disabled={!canAdvance()}
          style={{ flex: 1, background: canAdvance() ? theme.ink : theme.line, color: theme.paper, border: 'none', padding: '14px 20px', fontSize: 14, cursor: canAdvance() ? 'pointer' : 'not-allowed', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}>
          {isLastStep ? 'Finish' : 'Continue'}<ArrowRight size={16} />
        </button>
      </div>
    </div>
  );
}

// ============================================================================
// CLIENT VIEWS
// ============================================================================

function Section({ title, icon, children, action }) {
  return (
    <div style={{ marginBottom: '1.5rem' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12, color: theme.inkSoft }}>
        {icon}<span style={{ fontSize: 11, letterSpacing: '0.2em', textTransform: 'uppercase' }}>{title}</span>
        <div style={{ flex: 1, height: 1, background: theme.line }} />
        {action}
      </div>
      {children}
    </div>
  );
}

function EmptyState({ text, action, onAction }) {
  return (
    <div style={{ background: theme.paper, border: `1px dashed ${theme.line}`, padding: '20px', textAlign: 'center' }}>
      <div style={{ fontSize: 14, color: theme.inkSoft, marginBottom: 10 }}>{text}</div>
      {action && (<button onClick={onAction} style={{ background: 'transparent', border: `1px solid ${theme.ink}`, color: theme.ink, padding: '8px 14px', fontSize: 13, cursor: 'pointer' }}>{action}</button>)}
    </div>
  );
}

function HomeView({ session, updateData, setTab, onStartFlow }) {
  const { data } = session;
  const persona = PERSONAS[data.profile?.personaId] || PERSONAS.mentor;
  const today = new Date().toISOString().slice(0, 10);
  const hour = new Date().getHours();
  const greeting = hour < 12 ? 'Good morning' : hour < 17 ? 'Good afternoon' : 'Good evening';
  const greetingKey = hour < 12 ? 'morning' : hour < 17 ? 'afternoon' : 'evening';
  const todayHabits = (data.habitLog || {})[today] || {};
  const intention = (data.intentions || {})[today] || '';
  const toggleHabit = (habitId) => updateData({ ...data, habitLog: { ...(data.habitLog || {}), [today]: { ...todayHabits, [habitId]: !todayHabits[habitId] } } });

  const completed = (data.profile?.habits || []).filter(h => todayHabits[h.id]).length;
  const total = (data.profile?.habits || []).length;
  const streak = calcStreak(data.habitLog || {}, data.profile?.habits || []);
  const todayFlows = ((data.flowHistory || {})[today] || []).map(e => e.flowId);
  const showMorning = hour < 12 && !todayFlows.includes('morning');
  const showEvening = hour >= 17 && !todayFlows.includes('evening');
  const isMonday = new Date().getDay() === 1;

  let suggested = null;
  if (showMorning) suggested = { flow: 'morning', label: 'Start your day', note: '3-minute check-in to set your focus' };
  else if (showEvening) suggested = { flow: 'evening', label: 'Reflect on today', note: 'Close the day with intention' };
  else if (isMonday) suggested = { flow: 'weekly', label: 'Weekly review', note: 'Zoom out and plan the week' };

  return (
    <div style={{ padding: '2rem 1.5rem', maxWidth: 600, margin: '0 auto' }}>
      <div style={{ marginBottom: '1.5rem' }}>
        <div style={{ fontSize: 11, letterSpacing: '0.25em', textTransform: 'uppercase', color: theme.inkFaint, marginBottom: 6 }}>
          {new Date().toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long' })}
        </div>
        <h1 style={{ fontFamily: '"Fraunces", serif', fontWeight: 400, fontSize: '2.25rem', color: theme.ink, margin: 0, letterSpacing: '-0.02em', lineHeight: 1.1 }}>
          {greeting}, <em style={{ fontStyle: 'italic' }}>{data.profile?.name}</em>.
        </h1>
      </div>

      <div style={{ background: theme.paper, border: `1px solid ${theme.line}`, borderLeft: `3px solid ${persona.accent}`, padding: '18px', marginBottom: '1.25rem' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
          <div style={{ fontSize: 22, color: persona.accent, fontFamily: '"Fraunces", serif', lineHeight: 1 }}>{persona.glyph}</div>
          <div style={{ fontSize: 11, letterSpacing: '0.2em', textTransform: 'uppercase', color: theme.inkFaint }}>{persona.name}</div>
        </div>
        <div style={{ fontFamily: '"Fraunces", serif', fontSize: 19, color: theme.ink, lineHeight: 1.4, marginBottom: 14 }}>"{persona.greetings[greetingKey](data.profile?.name)}"</div>
        {suggested ? (
          <button onClick={() => onStartFlow(suggested.flow)}
            style={{ width: '100%', background: theme.ink, color: theme.paper, border: 'none', padding: '12px 16px', fontSize: 14, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <div style={{ textAlign: 'left' }}>
              <div style={{ fontSize: 14, marginBottom: 2 }}>{suggested.label}</div>
              <div style={{ fontSize: 11, opacity: 0.7 }}>{suggested.note}</div>
            </div>
            <ArrowRight size={16} />
          </button>
        ) : (
          <button onClick={() => setTab('chat')} style={{ width: '100%', background: 'transparent', border: `1px solid ${theme.line}`, color: theme.ink, padding: '10px 14px', fontSize: 13, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}>
            <MessageCircle size={14} /> Chat with your coach
          </button>
        )}
      </div>

      <Section title="Quick check-ins" icon={<Zap size={14} />}
        action={<HelpTip title="Guided check-ins">Structured flows using real coaching frameworks. Each ends with a personalised response from your coach.</HelpTip>}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 8 }}>
          {Object.values(FLOWS).map(flow => {
            const Icon = flow.icon;
            const done = todayFlows.includes(flow.id);
            return (
              <button key={flow.id} onClick={() => onStartFlow(flow.id)}
                style={{ background: theme.paper, border: `1px solid ${theme.line}`, padding: '14px', cursor: 'pointer', textAlign: 'left', position: 'relative' }}>
                <Icon size={18} color={persona.accent} style={{ marginBottom: 8 }} />
                <div style={{ fontFamily: '"Fraunces", serif', fontSize: 15, color: theme.ink, marginBottom: 2, lineHeight: 1.2 }}>{flow.title}</div>
                <div style={{ fontSize: 11, color: theme.inkSoft }}>{flow.estMinutes} min</div>
                {done && <div style={{ position: 'absolute', top: 8, right: 8, width: 16, height: 16, background: theme.success, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><Check size={10} color={theme.paper} strokeWidth={3} /></div>}
              </button>
            );
          })}
        </div>
      </Section>

      {intention && (
        <Section title="Today's focus" icon={<Sunrise size={14} />}>
          <div style={{ background: theme.paper, border: `1px solid ${theme.line}`, borderLeft: `2px solid ${persona.accent}`, padding: '14px 16px', fontFamily: '"Fraunces", serif', fontSize: 17, color: theme.ink, lineHeight: 1.4 }}>{intention}</div>
        </Section>
      )}

      <Section title={`Habits · ${completed}/${total}`} icon={<Target size={14} />}
        action={<HelpTip title="Habits">Tap to mark done. A habit's "done for the day" any time you toggle it — no pressure around specific timing.</HelpTip>}>
        {total === 0 ? (
          <EmptyState text="No habits set up yet." action="Add habits" onAction={() => setTab('goals')} />
        ) : (
          <>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              {(data.profile?.habits || []).map(h => {
                const done = !!todayHabits[h.id];
                return (
                  <button key={h.id} onClick={() => toggleHabit(h.id)}
                    style={{ display: 'flex', alignItems: 'center', gap: 12, background: done ? theme.bgDeep : theme.paper, border: `1px solid ${theme.line}`, padding: '12px 14px', cursor: 'pointer', textAlign: 'left', borderLeft: done ? `3px solid ${persona.accent}` : `3px solid transparent` }}>
                    <div style={{ width: 20, height: 20, border: `1px solid ${done ? persona.accent : theme.inkFaint}`, background: done ? persona.accent : 'transparent', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                      {done && <Check size={13} color={theme.paper} strokeWidth={3} />}
                    </div>
                    <span style={{ fontSize: 15, color: theme.ink, textDecoration: done ? 'line-through' : 'none', opacity: done ? 0.65 : 1 }}>{h.name}</span>
                  </button>
                );
              })}
            </div>
            {streak > 0 && (<div style={{ marginTop: 14, fontSize: 13, color: theme.inkSoft, display: 'flex', alignItems: 'center', gap: 6 }}><TrendingUp size={14} /> {streak} day streak</div>)}
          </>
        )}
      </Section>

      <Section title="Human coach" icon={<Calendar size={14} />}
        action={<HelpTip title="Your human coach">A paired professional who meets with you fortnightly. They see engagement signals, not your check-in content, unless you share.</HelpTip>}>
        {data.selectedCoach ? (
          <>
            <ClientHomeNudgePreview session={session} setTab={setTab} />
            <div style={{ background: theme.paper, border: `1px solid ${theme.line}`, padding: '14px 16px' }}>
              <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
                <div style={{ width: 40, height: 40, borderRadius: '50%', background: theme.bgDeep, display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: '"Fraunces", serif', fontSize: 14, color: theme.accent }}>
                  {data.selectedCoach.name.split(' ').map(n => n[0]).join('')}
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontFamily: '"Fraunces", serif', fontSize: 16, color: theme.ink }}>{data.selectedCoach.name}</div>
                  <div style={{ fontSize: 12, color: theme.inkSoft }}>Next session: to schedule</div>
                </div>
                <button onClick={() => setTab('coaches')} style={{ background: theme.ink, color: theme.paper, border: 'none', padding: '8px 12px', fontSize: 12, cursor: 'pointer' }}>Open</button>
              </div>
            </div>
          </>
        ) : data.briefPublished ? (
          <PendingCoachesHomeCallout session={session} setTab={setTab} />
        ) : (
          <EmptyState text="Paired with a human coach for fortnightly accountability." action="Browse coaches" onAction={() => setTab('coaches')} />
        )}
      </Section>
    </div>
  );
}

function ClientHomeNudgePreview({ session, setTab }) {
  const coachEmail = session.data.selectedCoach?.email;
  const linkId = session.data.selectedCoach?.linkId;
  const [latest, setLatest] = useState(null);

  useEffect(() => {
    const id = linkId || coachEmail;
    if (!id) return;
    getMessages(id, linkId ? null : session.id)
      .then(msgs => {
        const found = [...msgs].reverse().find(m => m.to === session.id);
        setLatest(found || null);
      })
      .catch(() => {});
  }, [coachEmail, linkId, session.id]);

  if (!latest) return null;

  const ago = timeAgo(latest.createdAt);

  return (
    <button onClick={() => setTab('coaches')}
      style={{ width: '100%', textAlign: 'left', background: latest.read ? theme.paper : '#FEF8EE', border: `1px solid ${latest.read ? theme.line : theme.accent}`, borderLeft: `3px solid ${theme.accent}`, padding: '14px', marginBottom: 10, cursor: 'pointer', display: 'block' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
        <MessageSquare size={13} color={theme.accent} />
        <span style={{ fontSize: 10, letterSpacing: '0.15em', textTransform: 'uppercase', color: theme.accent, fontWeight: 600 }}>
          {latest.read ? 'Last message from coach' : 'New message from coach'}
        </span>
        <span style={{ fontSize: 10, color: theme.inkFaint, marginLeft: 'auto' }}>{ago}</span>
      </div>
      <div style={{ fontFamily: '"Fraunces", serif', fontSize: 15, color: theme.ink, lineHeight: 1.4 }}>
        {latest.body.length > 140 ? latest.body.slice(0, 140) + '…' : latest.body}
      </div>
    </button>
  );
}

function PendingCoachesHomeCallout({ session, setTab }) {
  const [count, setCount] = useState(0);

  useEffect(() => {
    coachesApi.getPendingCoaches()
      .then(d => setCount((d.pendingCoaches || []).length))
      .catch(() => {});
  }, []);

  if (count === 0) {
    return (
      <InfoCallout icon={<Clock size={14} />} tone="info">
        <strong>Brief published.</strong> Coaches are reviewing your profile. Check back shortly.
      </InfoCallout>
    );
  }

  return (
    <button onClick={() => setTab('coaches')}
      style={{ width: '100%', textAlign: 'left', background: '#F0F7F0', border: `1px solid ${theme.success}`, borderLeft: `3px solid ${theme.success}`, padding: '14px', cursor: 'pointer', marginBottom: 10, display: 'block' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
        <CheckCircle2 size={14} color={theme.success} />
        <span style={{ fontSize: 11, letterSpacing: '0.15em', textTransform: 'uppercase', color: theme.success, fontWeight: 600 }}>
          {count} coach{count === 1 ? '' : 'es'} responded to your brief
        </span>
      </div>
      <div style={{ fontSize: 14, color: theme.ink, fontFamily: '"Fraunces", serif' }}>
        Review their profiles and choose your coach →
      </div>
    </button>
  );
}

function timeAgo(iso) {
  const ms = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(ms / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days < 7) return `${days}d ago`;
  return new Date(iso).toLocaleDateString('en-GB');
}

function calcStreak(log, habits) {
  if (habits.length === 0) return 0;
  let streak = 0;
  const d = new Date();
  for (let i = 0; i < 60; i++) {
    const key = d.toISOString().slice(0, 10);
    const day = log[key] || {};
    const done = habits.filter(h => day[h.id]).length;
    if (done > 0 && done / habits.length >= 0.5) streak++;
    else if (i > 0) break;
    d.setDate(d.getDate() - 1);
  }
  return streak;
}

function ChatView({ session, updateData, onStartFlow }) {
  const { data } = session;
  const persona = PERSONAS[data.profile?.personaId] || PERSONAS.mentor;
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [showSafety, setShowSafety] = useState(false);
  const scrollRef = useRef(null);
  const baseRef = useRef('');
  const { isListening, supported, toggle } = useDictation((transcript) => {
    setInput((baseRef.current + ' ' + transcript).trim().replace(/\s+/g, ' '));
  });

  useEffect(() => { if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight; }, [data.chat, sending]);

  const quickPrompts = ['I\'m procrastinating, help me start', 'I need perspective on something', 'Motivate me right now', 'I\'m overwhelmed'];

  const send = async (messageText) => {
    const text = (messageText || input).trim();
    if (!text || sending) return;
    if (detectDistress(text)) setShowSafety(true);

    const userMsg = { role: 'user', content: text, ts: Date.now() };
    const newChat = [...(data.chat || []), userMsg];
    updateData({ ...data, chat: newChat });
    setInput('');
    setSending(true);

    try {
      const today = new Date().toISOString().slice(0, 10);
      const todayHabits = data.habitLog?.[today] || {};
      const done = (data.profile?.habits || []).filter(h => todayHabits[h.id]).map(h => h.name);
      const pending = (data.profile?.habits || []).filter(h => !todayHabits[h.id]).map(h => h.name);
      const todayFlows = (data.flowHistory?.[today] || []).map(e => e.flowId);

      const context = {
        name: data.profile?.name,
        intention: data.intentions?.[today] || '',
        habitsDone: done,
        habitsPending: pending,
        flowsDone: todayFlows,
        goals: data.profile?.goals || [],
      };

      const apiMessages = newChat.map(m => ({ role: m.role, content: m.content }));

      const result = await aiApi.chat({
        messages: apiMessages,
        personaId: persona.id,
        context,
      });
      const replyText = result.text || 'Sorry, something went wrong.';
      if (result.distressDetected) setShowSafety(true);
      updateData({ ...data, chat: [...newChat, { role: 'assistant', content: replyText, ts: Date.now() }] });
    } catch (e) {
      updateData({ ...data, chat: [...newChat, { role: 'assistant', content: 'Connection hiccup. Try again in a moment.', ts: Date.now() }] });
    } finally { setSending(false); }
  };

  const chat = data.chat || [];
  const handleMicToggle = () => { if (!isListening) baseRef.current = input; toggle(); };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: 'calc(100vh - 76px)', maxWidth: 600, margin: '0 auto' }}>
      <div style={{ padding: '1.5rem 1.5rem 1rem', borderBottom: `1px solid ${theme.line}`, background: theme.bg }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <div style={{ fontSize: 28, color: persona.accent, fontFamily: '"Fraunces", serif', lineHeight: 1 }}>{persona.glyph}</div>
          <div style={{ flex: 1 }}>
            <div style={{ fontFamily: '"Fraunces", serif', fontSize: 18, color: theme.ink }}>{persona.name}</div>
            <div style={{ fontSize: 12, color: theme.inkSoft, fontStyle: 'italic', fontFamily: '"Fraunces", serif' }}>{persona.tagline}</div>
          </div>
          <HelpTip title="Your AI coach">Knows your habits, intentions, and goals. Responses are generated in real time and are private to you. Use guided check-ins below for structured reflection.</HelpTip>
        </div>
      </div>

      <div ref={scrollRef} style={{ flex: 1, overflowY: 'auto', padding: '1rem 1.5rem' }}>
        {chat.length === 0 && (
          <div>
            <div style={{ textAlign: 'center', marginTop: '1.5rem', marginBottom: '2rem', padding: '1rem' }}>
              <div style={{ fontSize: 48, color: persona.accent, fontFamily: '"Fraunces", serif', marginBottom: 16, lineHeight: 1 }}>{persona.glyph}</div>
              <div style={{ fontFamily: '"Fraunces", serif', fontStyle: 'italic', fontSize: 18, color: theme.ink, marginBottom: 8, lineHeight: 1.4 }}>{openingLine(persona, data.profile?.name || '')}</div>
              <div style={{ fontSize: 13, color: theme.inkFaint }}>Start a conversation, or try one of these:</div>
            </div>
            <div style={{ fontSize: 10, letterSpacing: '0.2em', textTransform: 'uppercase', color: theme.inkFaint, marginBottom: 10 }}>Guided check-ins</div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 8, marginBottom: 24 }}>
              {Object.values(FLOWS).map(flow => {
                const Icon = flow.icon;
                return (
                  <button key={flow.id} onClick={() => onStartFlow(flow.id)}
                    style={{ background: theme.paper, border: `1px solid ${theme.line}`, padding: '12px', cursor: 'pointer', textAlign: 'left', display: 'flex', alignItems: 'center', gap: 10 }}>
                    <Icon size={16} color={persona.accent} />
                    <div>
                      <div style={{ fontSize: 13, color: theme.ink }}>{flow.title}</div>
                      <div style={{ fontSize: 10, color: theme.inkFaint }}>{flow.estMinutes} min</div>
                    </div>
                  </button>
                );
              })}
            </div>
            <div style={{ fontSize: 10, letterSpacing: '0.2em', textTransform: 'uppercase', color: theme.inkFaint, marginBottom: 10 }}>Or ask</div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              {quickPrompts.map(p => (
                <button key={p} onClick={() => send(p)} style={{ background: 'transparent', border: `1px solid ${theme.line}`, padding: '10px 14px', fontFamily: '"Fraunces", serif', fontSize: 15, color: theme.ink, textAlign: 'left', cursor: 'pointer', fontStyle: 'italic' }}>"{p}"</button>
              ))}
            </div>
          </div>
        )}

        {chat.map((m, i) => (
          <div key={i} style={{ marginBottom: 16, display: 'flex', justifyContent: m.role === 'user' ? 'flex-end' : 'flex-start' }}>
            <div style={{ maxWidth: '85%', background: m.role === 'user' ? theme.ink : theme.paper, color: m.role === 'user' ? theme.paper : theme.ink, padding: '12px 14px', fontFamily: m.role === 'user' ? 'Inter, sans-serif' : '"Fraunces", serif', fontSize: m.role === 'user' ? 14 : 16, lineHeight: 1.5, border: m.role === 'user' ? 'none' : `1px solid ${theme.line}`, borderLeft: m.role === 'assistant' ? `2px solid ${persona.accent}` : 'none', whiteSpace: 'pre-wrap' }}>{m.content}</div>
          </div>
        ))}

        {sending && (<div style={{ display: 'flex', alignItems: 'center', gap: 8, color: theme.inkFaint, fontSize: 13, fontStyle: 'italic', padding: '0 4px' }}><Sparkles size={14} /> {persona.name} is thinking...</div>)}
      </div>

      {showSafety && <SafetyHandoff onDismiss={() => setShowSafety(false)} />}

      <div style={{ padding: '12px 16px', background: theme.bg, borderTop: `1px solid ${theme.line}`, display: 'flex', gap: 8, alignItems: 'center' }}>
        <div style={{ flex: 1, position: 'relative' }}>
          <input value={input} onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); } }}
            placeholder={isListening ? 'Listening...' : 'Type or dictate...'} disabled={sending}
            style={{ width: '100%', background: theme.paper, border: `1px solid ${isListening ? theme.accent : theme.line}`, padding: '12px 44px 12px 14px', fontSize: 15, color: theme.ink, outline: 'none', boxSizing: 'border-box' }} />
          {supported && (
            <button onClick={handleMicToggle} type="button"
              style={{ position: 'absolute', top: '50%', right: 8, transform: 'translateY(-50%)', width: 28, height: 28, background: isListening ? theme.accent : 'transparent', border: 'none', borderRadius: '50%', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', color: isListening ? theme.paper : theme.inkSoft }}>
              {isListening ? <MicOff size={13} /> : <Mic size={13} />}
            </button>
          )}
        </div>
        <button onClick={() => send()} disabled={sending || !input.trim()}
          style={{ background: theme.ink, color: theme.paper, border: 'none', padding: '0 16px', height: 44, cursor: sending ? 'not-allowed' : 'pointer', opacity: (sending || !input.trim()) ? 0.5 : 1 }}>
          <Send size={16} />
        </button>
      </div>
    </div>
  );
}

function openingLine(persona, name) {
  return { mentor: `What's on your mind, ${name}?`, friend: `Hey ${name}, how's it going?`, sergeant: `${name}. What are we working on today?`, guide: `What would be most useful to explore right now?` }[persona.id];
}

function SafetyHandoff({ onDismiss }) {
  return (
    <div style={{ margin: '0 16px 12px', background: '#FDF5F3', border: `1px solid ${theme.danger}`, padding: '14px' }}>
      <div style={{ display: 'flex', gap: 10, alignItems: 'flex-start', marginBottom: 10 }}>
        <AlertTriangle size={18} color={theme.danger} style={{ flexShrink: 0, marginTop: 2 }} />
        <div>
          <div style={{ fontFamily: '"Fraunces", serif', fontSize: 15, fontWeight: 500, color: theme.danger, marginBottom: 4 }}>This sounds heavy.</div>
          <div style={{ fontSize: 13, color: theme.ink, lineHeight: 1.5 }}>YourCoach isn't a mental health service. Please reach out to someone qualified:</div>
        </div>
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6, fontSize: 13, color: theme.ink, paddingLeft: 28 }}>
        <div>• <strong>Samaritans</strong>: 116 123 (free, 24/7)</div>
        <div>• <strong>NHS 111</strong>: mental health option</div>
        <div>• <strong>Text SHOUT</strong>: 85258</div>
        <div>• Speak to your GP</div>
      </div>
      <button onClick={onDismiss} style={{ marginTop: 10, background: 'transparent', border: 'none', color: theme.inkSoft, fontSize: 12, cursor: 'pointer', textDecoration: 'underline', paddingLeft: 28 }}>Dismiss</button>
    </div>
  );
}

function GoalsView({ session, updateData }) {
  const { data } = session;
  const [draft, setDraft] = useState('');
  const suggestions = ['Morning movement', 'Read 20 minutes', 'No phone before bed', 'Drink 2L water', 'Journal 5 minutes', 'Meditate 10 min', '10,000 steps', 'Sleep by 11pm'];

  const addHabit = (name) => {
    if (!name.trim() || (data.profile?.habits || []).some(h => h.name === name.trim())) return;
    updateData({ ...data, profile: { ...data.profile, habits: [...(data.profile?.habits || []), { id: Date.now().toString() + Math.random(), name: name.trim() }] } });
  };
  const removeHabit = (id) => updateData({ ...data, profile: { ...data.profile, habits: data.profile.habits.filter(h => h.id !== id) } });
  const weekData = last7Days(data.habitLog || {}, data.profile?.habits || []);
  const weekTotal = weekData.reduce((s, d) => s + d.done, 0);
  const weekPossible = weekData.reduce((s, d) => s + d.total, 0);
  const weekPct = weekPossible > 0 ? Math.round((weekTotal / weekPossible) * 100) : 0;

  return (
    <div style={{ padding: '2rem 1.5rem', maxWidth: 600, margin: '0 auto' }}>
      <div style={{ fontSize: 11, letterSpacing: '0.25em', textTransform: 'uppercase', color: theme.inkFaint, marginBottom: 6 }}>Your habits & goals</div>
      <h1 style={{ fontFamily: '"Fraunces", serif', fontWeight: 400, fontSize: '2rem', color: theme.ink, margin: 0, marginBottom: '1.75rem', letterSpacing: '-0.02em' }}>What you're building</h1>

      <Section title="This week">
        <div style={{ background: theme.paper, border: `1px solid ${theme.line}`, padding: '16px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 12 }}>
            <span style={{ fontFamily: '"Fraunces", serif', fontSize: 36, color: theme.ink, lineHeight: 1 }}>{weekPct}%</span>
            <span style={{ fontSize: 13, color: theme.inkSoft }}>{weekTotal} of {weekPossible} completed</span>
          </div>
          <div style={{ display: 'flex', gap: 3, height: 42, alignItems: 'flex-end' }}>
            {weekData.map((d, i) => {
              const pct = d.total > 0 ? d.done / d.total : 0;
              return (
                <div key={i} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4 }}>
                  <div style={{ flex: 1, width: '100%', display: 'flex', alignItems: 'flex-end' }}>
                    <div style={{ width: '100%', height: `${Math.max(pct * 100, 6)}%`, background: pct > 0 ? theme.ink : theme.line, opacity: pct > 0 ? (0.3 + pct * 0.7) : 1 }} />
                  </div>
                  <div style={{ fontSize: 10, color: theme.inkFaint }}>{d.label}</div>
                </div>
              );
            })}
          </div>
        </div>
      </Section>

      <Section title="Habits">
        <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
          <input value={draft} onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter' && draft.trim()) { addHabit(draft); setDraft(''); } }}
            placeholder="Add a new habit..."
            style={{ flex: 1, background: theme.paper, border: `1px solid ${theme.line}`, padding: '12px 14px', fontSize: 14, color: theme.ink, outline: 'none' }} />
          <button onClick={() => { if (draft.trim()) { addHabit(draft); setDraft(''); } }} style={{ background: theme.ink, color: theme.paper, border: 'none', padding: '0 16px', cursor: 'pointer' }}><Plus size={16} /></button>
        </div>
        <div style={{ marginBottom: 16 }}>
          <div style={{ fontSize: 11, letterSpacing: '0.1em', textTransform: 'uppercase', color: theme.inkFaint, marginBottom: 8 }}>Quick add</div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
            {suggestions.map(s => {
              const added = (data.profile?.habits || []).some(h => h.name === s);
              return (
                <button key={s} onClick={() => !added && addHabit(s)} disabled={added}
                  style={{ background: added ? theme.bgDeep : 'transparent', border: `1px solid ${added ? theme.accent : theme.line}`, padding: '6px 12px', fontSize: 13, color: added ? theme.accent : theme.inkSoft, cursor: added ? 'default' : 'pointer', borderRadius: 999, opacity: added ? 0.6 : 1 }}>
                  {added ? '✓' : '+'} {s}
                </button>
              );
            })}
          </div>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          {(data.profile?.habits || []).map(h => (
            <div key={h.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: theme.paper, border: `1px solid ${theme.line}`, borderLeft: `3px solid ${theme.accent}`, padding: '12px 14px', fontSize: 15, color: theme.ink }}>
              {h.name}
              <button onClick={() => removeHabit(h.id)} style={{ background: 'none', border: 'none', color: theme.inkFaint, cursor: 'pointer', fontSize: 20 }}>×</button>
            </div>
          ))}
        </div>
      </Section>
    </div>
  );
}

function last7Days(log, habits) {
  const days = [];
  const d = new Date();
  const dayLabels = ['S', 'M', 'T', 'W', 'T', 'F', 'S'];
  for (let i = 6; i >= 0; i--) {
    const date = new Date(); date.setDate(d.getDate() - i);
    const key = date.toISOString().slice(0, 10);
    const day = log[key] || {};
    const done = habits.filter(h => day[h.id]).length;
    days.push({ done, total: habits.length, label: dayLabels[date.getDay()] });
  }
  return days;
}

// ============================================================================
// COACHES VIEW — the layered match mechanism
// ============================================================================

function ActiveCoachingPanel({ session, updateData }) {
  const coach = session.data.selectedCoach;
  const [pane, setPane] = useState('overview');
  // Use the real linkId from DB — fall back gracefully for demo seed coaches
  const linkId = coach.linkId || null;
  const coachEmail = coach.email; // kept for display only
  const [unread, setUnread] = useState(0);

  useEffect(() => {
    if (!linkId) return;
    getMessages(linkId, null).then(m =>
      setUnread(m.filter(msg => msg.to === session.id && !msg.read).length)
    ).catch(() => {});
  }, [linkId, session.id]);

  useEffect(() => {
    if (pane === 'messages' && linkId) markMessagesRead(linkId, null, session.id).catch(() => {});
  }, [pane]);

  return (
    <div style={{ background: theme.paper, border: `1px solid ${theme.line}`, marginBottom: 20 }}>
      <div style={{ padding: '14px 16px', display: 'flex', gap: 12, alignItems: 'center', borderBottom: `1px solid ${theme.line}` }}>
        <div style={{ width: 42, height: 42, borderRadius: '50%', background: theme.bgDeep, display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: '"Fraunces", serif', fontSize: 14, color: theme.accent }}>
          {coach.name.split(' ').map(n => n[0]).join('')}
        </div>
        <div style={{ flex: 1 }}>
          <div style={{ fontFamily: '"Fraunces", serif', fontSize: 17, color: theme.ink }}>{coach.name}</div>
          <div style={{ fontSize: 12, color: theme.inkFaint }}>{coach.credential}</div>
        </div>
        <div style={{ fontSize: 11, color: theme.success, letterSpacing: '0.1em', textTransform: 'uppercase', fontWeight: 600 }}>Your coach</div>
      </div>

      <div style={{ display: 'flex', borderBottom: `1px solid ${theme.line}` }}>
        {[
          { id: 'overview', label: 'Overview', icon: User },
          { id: 'messages', label: 'Nudges', icon: MessageSquare, badge: unread },
          { id: 'share', label: 'Share', icon: Share2 },
        ].map(t => {
          const active = pane === t.id;
          const Icon = t.icon;
          return (
            <button key={t.id} onClick={() => setPane(t.id)}
              style={{ flex: 1, background: 'transparent', border: 'none', padding: '11px', fontSize: 12, cursor: 'pointer', color: active ? theme.ink : theme.inkFaint, borderBottom: active ? `2px solid ${theme.accent}` : '2px solid transparent', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6, fontWeight: active ? 600 : 400, position: 'relative' }}>
              <Icon size={13} /> {t.label}
              {t.badge > 0 && <span style={{ background: theme.danger, color: theme.paper, fontSize: 9, padding: '2px 5px', borderRadius: 10, fontWeight: 600 }}>{t.badge}</span>}
            </button>
          );
        })}
      </div>

      <div style={{ padding: '16px' }}>
        {pane === 'overview' && <ClientCoachOverview coach={coach} />}
        {pane === 'messages' && (linkId
          ? <ClientMessagesPane session={session} linkId={linkId} coachName={coach.name} />
          : <EmptyState text="Messaging available once connected to a live coach." />
        )}
        {pane === 'share' && (linkId
          ? <ShareWithCoachPane session={session} updateData={updateData} linkId={linkId} />
          : <EmptyState text="Sharing available once connected to a live coach." />
        )}
      </div>
    </div>
  );
}

function ClientCoachOverview({ coach }) {
  return (
    <div>
      <p style={{ fontSize: 14, color: theme.ink, lineHeight: 1.6, margin: 0, marginBottom: 14 }}>{coach.bio}</p>
      <div style={{ fontSize: 13, color: theme.inkSoft, fontStyle: 'italic', fontFamily: '"Fraunces", serif', marginBottom: 14 }}>{coach.specialism}</div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', paddingTop: 14, borderTop: `1px solid ${theme.line}` }}>
        <div style={{ fontSize: 12, color: theme.inkFaint }}>Session rate</div>
        <div style={{ fontFamily: '"Fraunces", serif', fontSize: 18, color: theme.ink }}>£{coach.rate}</div>
      </div>
      <InfoCallout icon={<Clock size={14} />} tone="info">
        Session booking and calendar integration coming soon. For now, your coach can reach out via nudges.
      </InfoCallout>
    </div>
  );
}

function ClientMessagesPane({ session, linkId, coachName }) {
  const [msgs, setMsgs] = useState([]);
  useEffect(() => {
    if (linkId) getMessages(linkId, null).then(setMsgs).catch(() => {});
  }, [linkId]);
  const [draft, setDraft] = useState('');
  const scrollRef = useRef(null);

  useEffect(() => { if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight; }, [msgs]);

  const reply = async () => {
    if (!draft.trim() || !linkId) return;
    try {
      const result = await messagesApi.send(linkId, {
        body: draft.trim(),
        kind: 'reply',
        recipientId: session.data.selectedCoach?.id,
      });
      if (result.message) setMsgs(prev => [...prev, result.message]);
    } catch (e) { console.error('Send error:', e.message); }
    setDraft('');
  };

  return (
    <div>
      <InfoCallout icon={<Info size={14} />} tone="info">
        <strong>Nudges from your coach</strong> appear here. Short, between-session prompts — reply when useful, ignore when not. Not for crisis support.
      </InfoCallout>

      <div ref={scrollRef} style={{ background: theme.bg, border: `1px solid ${theme.line}`, padding: '12px', maxHeight: 340, overflowY: 'auto', marginBottom: 12 }}>
        {msgs.length === 0 ? (
          <div style={{ fontSize: 13, color: theme.inkFaint, textAlign: 'center', padding: '20px 10px' }}>No messages yet. Your coach will reach out occasionally between sessions.</div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {msgs.map(m => {
              const fromMe = m.from === session.id;
              return (
                <div key={m.id} style={{ display: 'flex', justifyContent: fromMe ? 'flex-end' : 'flex-start' }}>
                  <div style={{ maxWidth: '82%' }}>
                    {m.kind && !fromMe && m.kind !== 'reply' && (<div style={{ fontSize: 9, letterSpacing: '0.15em', textTransform: 'uppercase', color: theme.inkFaint, marginBottom: 3 }}>{m.kind}</div>)}
                    <div style={{ background: fromMe ? theme.ink : theme.paper, color: fromMe ? theme.paper : theme.ink, padding: '10px 12px', fontSize: 14, lineHeight: 1.5, whiteSpace: 'pre-wrap', fontFamily: fromMe ? 'Inter, sans-serif' : '"Fraunces", serif', border: fromMe ? 'none' : `1px solid ${theme.line}` }}>{m.body}</div>
                    <div style={{ fontSize: 10, color: theme.inkFaint, marginTop: 3, textAlign: fromMe ? 'right' : 'left' }}>{timeAgo(m.createdAt)}</div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      <DictableText value={draft} onChange={setDraft} placeholder={`Reply to ${coachName.split(' ')[0]}...`} rows={2} />
      <button onClick={reply} disabled={!draft.trim()}
        style={{ width: '100%', background: draft.trim() ? theme.ink : theme.line, color: theme.paper, border: 'none', padding: '10px', fontSize: 13, cursor: draft.trim() ? 'pointer' : 'not-allowed', marginTop: 8, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}>
        <Send size={13} /> Send reply
      </button>
    </div>
  );
}

function ShareWithCoachPane({ session, updateData, linkId }) {
  const [existing, setExisting] = useState(null);
  useEffect(() => {
    if (linkId) coachesApi.getSharedContext(linkId).then(d => setExisting(d.sharedContext)).catch(() => {});
  }, [linkId]);
  const [agenda, setAgenda] = useState('');
  const [flagged, setFlagged] = useState([]);
  const [flagDraft, setFlagDraft] = useState('');
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    if (existing) {
      setAgenda(existing.sessionAgenda || '');
      setFlagged(existing.flaggedTopics || []);
    }
  }, [existing]);

  const save = async () => {
    if (!linkId) return;
    await coachesApi.saveSharedContext(linkId, {
      agenda: agenda.trim(),
      flaggedTopics: flagged,
    });
    setSaved(true);
    setTimeout(() => setSaved(false), 2500);
  };

  const clear = async () => {
    if (!linkId) return;
    try { await coachesApi.saveSharedContext(linkId, { agenda: '', flaggedTopics: [] }); } catch {}
    setAgenda(''); setFlagged([]); setExisting(null);
  };

  return (
    <div>
      <InfoCallout icon={<Shield size={14} />} tone="success">
        <strong>You control what your coach sees.</strong> They only see what you explicitly share here. Your raw check-ins, chats, and reflections stay private.
      </InfoCallout>

      <div style={{ marginBottom: 16 }}>
        <div style={{ fontSize: 11, letterSpacing: '0.1em', textTransform: 'uppercase', color: theme.inkFaint, marginBottom: 6, display: 'flex', alignItems: 'center', gap: 6 }}>
          What would you like to discuss next session?
          <HelpTip title="Session agenda">One or two sentences on what's on your mind. This helps your coach prepare so session time goes further.</HelpTip>
        </div>
        <DictableText value={agenda} onChange={setAgenda} placeholder="The thing you want to focus on..." rows={3} />
      </div>

      <div style={{ marginBottom: 16 }}>
        <div style={{ fontSize: 11, letterSpacing: '0.1em', textTransform: 'uppercase', color: theme.inkFaint, marginBottom: 6 }}>Topics on your mind (optional)</div>
        <div style={{ display: 'flex', gap: 6, marginBottom: 8 }}>
          <input value={flagDraft} onChange={(e) => setFlagDraft(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter' && flagDraft.trim()) { setFlagged([...flagged, flagDraft.trim()]); setFlagDraft(''); } }}
            placeholder="Add a topic..."
            style={{ flex: 1, background: theme.paper, border: `1px solid ${theme.line}`, padding: '8px 10px', fontSize: 13, color: theme.ink, outline: 'none' }} />
          <button onClick={() => { if (flagDraft.trim()) { setFlagged([...flagged, flagDraft.trim()]); setFlagDraft(''); } }}
            style={{ background: theme.ink, color: theme.paper, border: 'none', padding: '0 12px', cursor: 'pointer', fontSize: 13 }}>
            <Plus size={14} />
          </button>
        </div>
        {flagged.length > 0 && (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
            {flagged.map((f, i) => (
              <span key={i} style={{ background: theme.bgDeep, padding: '4px 10px', fontSize: 12, color: theme.ink, borderRadius: 999, display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                {f}
                <button onClick={() => setFlagged(flagged.filter((_, j) => j !== i))} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0, color: theme.inkFaint, display: 'flex', alignItems: 'center' }}><X size={11} /></button>
              </span>
            ))}
          </div>
        )}
      </div>

      <div style={{ display: 'flex', gap: 8 }}>
        {existing && (<button onClick={clear} style={{ background: 'transparent', border: `1px solid ${theme.line}`, color: theme.inkSoft, padding: '10px', fontSize: 13, cursor: 'pointer' }}>Clear</button>)}
        <button onClick={save} disabled={saved}
          style={{ flex: 1, background: saved ? theme.success : theme.ink, color: theme.paper, border: 'none', padding: '10px', fontSize: 13, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}>
          {saved ? (<><Check size={14} /> Shared with coach</>) : (<><Share2 size={13} /> {existing ? 'Update share' : 'Share with coach'}</>)}
        </button>
      </div>

      {existing && (
        <div style={{ marginTop: 12, fontSize: 11, color: theme.inkFaint, textAlign: 'center' }}>
          Last updated {timeAgo(existing.sharedAt)}
        </div>
      )}
    </div>
  );
}

function PendingCoachesPanel({ session, updateData, data }) {
  const [pendingCoaches, setPendingCoaches] = useState([]);
  const [confirming, setConfirming] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    coachesApi.getPendingCoaches()
      .then(d => setPendingCoaches(d.pendingCoaches || []))
      .catch(() => {})
      .finally(() => setLoading(false));

    // Poll every 30s for new responses
    const interval = setInterval(() => {
      coachesApi.getPendingCoaches()
        .then(d => setPendingCoaches(d.pendingCoaches || []))
        .catch(() => {});
    }, 30000);
    return () => clearInterval(interval);
  }, []);

  const confirm = async (linkId, coach) => {
    setConfirming(linkId);
    try {
      const result = await coachesApi.confirmCoach(linkId);
      if (result.confirmed) {
        const selectedCoach = {
          id: coach.coach_id,
          name: coach.name,
          credential: coach.credential,
          specialism: coach.specialism,
          bio: coach.bio,
          rate: coach.rate_gbp,
          axes: coach.axes,
          email: coach.coach_id,
          linkId,
        };
        updateData({ ...data, selectedCoach, briefPublished: false });
      }
    } catch (e) {
      window.alert('Could not confirm coach: ' + e.message);
    } finally {
      setConfirming(null);
    }
  };

  if (loading) {
    return (
      <InfoCallout icon={<Clock size={14} />} tone="info">
        Checking for coach responses...
      </InfoCallout>
    );
  }

  if (pendingCoaches.length === 0) {
    return (
      <InfoCallout icon={<Clock size={14} />} tone="info">
        <strong>Brief published.</strong> Coaches are reviewing your profile. We'll show their responses here as they come in — check back shortly.
      </InfoCallout>
    );
  }

  return (
    <div style={{ marginBottom: 24 }}>
      <div style={{ fontSize: 11, letterSpacing: '0.2em', textTransform: 'uppercase', color: theme.success, marginBottom: 8 }}>
        {pendingCoaches.length} coach{pendingCoaches.length === 1 ? '' : 'es'} responded to your brief
      </div>
      <InfoCallout icon={<Info size={14} />} tone="success">
        These coaches have reviewed your brief and want to work with you. Review their profiles and confirm one to get started.
      </InfoCallout>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        {pendingCoaches.map(coach => (
          <div key={coach.link_id} style={{ background: theme.paper, border: `1px solid ${theme.success}`, borderLeft: `3px solid ${theme.success}`, padding: '16px' }}>
            <div style={{ display: 'flex', gap: 14, marginBottom: 12 }}>
              <div style={{ width: 48, height: 48, borderRadius: '50%', background: theme.bgDeep, display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: '"Fraunces", serif', fontSize: 18, color: theme.accent, flexShrink: 0 }}>
                {coach.name?.split(' ').map(n => n[0]).join('')}
              </div>
              <div style={{ flex: 1 }}>
                <div style={{ fontFamily: '"Fraunces", serif', fontSize: 18, color: theme.ink }}>{coach.name}</div>
                <div style={{ fontSize: 12, color: theme.inkFaint }}>{coach.credential}</div>
                <div style={{ fontSize: 13, color: theme.inkSoft, fontStyle: 'italic', fontFamily: '"Fraunces", serif' }}>{coach.specialism}</div>
              </div>
            </div>
            <p style={{ fontSize: 14, color: theme.ink, lineHeight: 1.5, margin: 0, marginBottom: 12 }}>{coach.bio}</p>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', paddingTop: 12, borderTop: `1px solid ${theme.line}` }}>
              <div style={{ fontSize: 13, color: theme.inkSoft }}>
                <span style={{ fontFamily: '"Fraunces", serif', fontSize: 17, color: theme.ink }}>£{coach.rate_gbp}</span> / session
              </div>
              <button
                onClick={() => confirm(coach.link_id, coach)}
                disabled={confirming === coach.link_id}
                style={{ background: theme.success, color: theme.paper, border: 'none', padding: '10px 18px', fontSize: 13, cursor: confirming ? 'wait' : 'pointer', opacity: confirming ? 0.7 : 1, display: 'flex', alignItems: 'center', gap: 6 }}>
                <Check size={14} /> {confirming === coach.link_id ? 'Confirming...' : 'Choose this coach'}
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function CoachesView({ session, updateData }) {
  const { data } = session;
  const [serverCoaches, setServerCoaches] = useState([]);
  const [loadingCoaches, setLoadingCoaches] = useState(true);

  useEffect(() => {
    coachesApi.getMatches()
      .then(d => setServerCoaches(d.coaches || []))
      .catch(() => setServerCoaches([]))
      .finally(() => setLoadingCoaches(false));
  }, []);

  // Combine seed coaches with server coaches for matching
  const allCoaches = [...SEED_COACHES, ...serverCoaches.filter(c => !SEED_COACHES.find(s => s.email === c.id))];
  const tiers = tieredMatches(data.profile?.axes || {}, allCoaches);
  const hasStrongMatches = tiers.excellent.length >= 2;

  const selectCoach = async (coach) => {
    const stamped = coach.email ? coach : { ...coach, email: `demo_${coach.id}@yourcoach.local`, isSeed: true };
    try {
      if (!stamped.isSeed) {
        await coachesApi.selectCoach(coach.id || coach.user_id);
        // Fetch the real linkId from the server
        const pending = await coachesApi.getPendingCoaches().catch(() => ({ pendingCoaches: [] }));
        const link = pending.pendingCoaches?.find(c => c.coach_id === (coach.id || coach.user_id));
        if (link) stamped.linkId = link.link_id;
      }
    } catch {}
    updateData({ ...data, selectedCoach: stamped });
  };

  const publishBrief = async () => {
    try {
      await coachesApi.publishBrief();
    } catch {}
    updateData({ ...data, briefPublished: true });
  };

  return (
    <div style={{ padding: '2rem 1.5rem', maxWidth: 600, margin: '0 auto' }}>
      <div style={{ fontSize: 11, letterSpacing: '0.25em', textTransform: 'uppercase', color: theme.inkFaint, marginBottom: 6 }}>Human coaches</div>
      <h1 style={{ fontFamily: '"Fraunces", serif', fontWeight: 400, fontSize: '2rem', color: theme.ink, margin: 0, marginBottom: '0.75rem', letterSpacing: '-0.02em' }}>Paired with people, not just AI.</h1>
      <p style={{ fontSize: 14, color: theme.inkSoft, lineHeight: 1.6, marginBottom: '1.5rem' }}>Fortnightly sessions for real accountability. Matched to how you work.</p>

      {data.selectedCoach && (
        <ActiveCoachingPanel session={session} updateData={updateData} />
      )}

      {!data.selectedCoach && data.briefPublished && (
        <PendingCoachesPanel session={session} updateData={updateData} data={data} />
      )}

      {!hasStrongMatches && !data.briefPublished && !data.selectedCoach && (
        <InfoCallout icon={<Info size={14} />} tone="accent">
          <div style={{ marginBottom: 8 }}><strong>Few excellent matches right now.</strong> We have {tiers.excellent.length} coach{tiers.excellent.length === 1 ? '' : 'es'} who closely match your style, plus {tiers.good.length + tiers.stretch.length} stretch matches below.</div>
          <div style={{ fontSize: 12, color: theme.inkSoft, marginBottom: 10 }}>You can also let us share an anonymised brief with coaches who aren't auto-matched. They can opt in if you're a fit for their practice.</div>
          <button onClick={publishBrief}
            style={{ background: theme.accent, color: theme.paper, border: 'none', padding: '8px 12px', fontSize: 12, cursor: 'pointer' }}>
            Publish my brief to coaches
          </button>
        </InfoCallout>
      )}

      {tiers.excellent.length > 0 && (
        <>
          <div style={{ fontSize: 11, letterSpacing: '0.2em', textTransform: 'uppercase', color: theme.accent, marginBottom: 8, marginTop: 24 }}>◈ Excellent matches</div>
          <div style={{ fontSize: 12, color: theme.inkSoft, marginBottom: 12 }}>These coaches closely match your style preferences.</div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12, marginBottom: 20 }}>
            {tiers.excellent.map(c => <CoachCard key={c.id} coach={c} featured selected={data.selectedCoach?.id === c.id} onSelect={selectCoach} showScore />)}
          </div>
        </>
      )}

      {tiers.good.length > 0 && (
        <>
          <div style={{ fontSize: 11, letterSpacing: '0.2em', textTransform: 'uppercase', color: theme.inkSoft, marginBottom: 8, marginTop: 24 }}>Good matches</div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12, marginBottom: 20 }}>
            {tiers.good.map(c => <CoachCard key={c.id} coach={c} selected={data.selectedCoach?.id === c.id} onSelect={selectCoach} showScore />)}
          </div>
        </>
      )}

      {tiers.stretch.length > 0 && (
        <>
          <div style={{ fontSize: 11, letterSpacing: '0.2em', textTransform: 'uppercase', color: theme.inkFaint, marginBottom: 8, marginTop: 24 }}>Stretch matches</div>
          <InfoCallout icon={<Info size={14} />} tone="info">
            <strong>Stretch matches</strong> have a different natural style from yours. Sometimes this is exactly what clients need — being challenged outside your usual comfort zone. Consider only if you're open to something different.
          </InfoCallout>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            {tiers.stretch.map(c => <CoachCard key={c.id} coach={c} selected={data.selectedCoach?.id === c.id} onSelect={selectCoach} showScore stretch />)}
          </div>
        </>
      )}
    </div>
  );
}

function CoachCard({ coach, featured, selected, onSelect, showScore, stretch }) {
  const initials = (coach.name || '?').split(' ').map(n => n[0]).join('');
  const archetype = coach.axes ? recommendPersona(coach.axes) : null;
  return (
    <div style={{ background: theme.paper, border: `1px solid ${selected ? theme.success : (featured ? theme.accent : theme.line)}`, borderLeft: selected ? `3px solid ${theme.success}` : (featured ? `3px solid ${theme.accent}` : `1px solid ${theme.line}`), padding: '16px', opacity: stretch ? 0.85 : 1 }}>
      <div style={{ display: 'flex', gap: 14, marginBottom: 12 }}>
        <div style={{ width: 48, height: 48, borderRadius: '50%', background: theme.bgDeep, display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: '"Fraunces", serif', fontSize: 18, color: theme.accentDeep, flexShrink: 0 }}>{initials}</div>
        <div style={{ flex: 1 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
            <div style={{ fontFamily: '"Fraunces", serif', fontSize: 18, color: theme.ink }}>{coach.name}</div>
            {archetype && (
              <span style={{ fontSize: 10, color: archetype.accent, border: `1px solid ${archetype.accent}`, padding: '2px 6px', display: 'flex', alignItems: 'center', gap: 4 }}>
                <span style={{ fontFamily: '"Fraunces", serif' }}>{archetype.glyph}</span> {archetype.name}
              </span>
            )}
            {showScore && coach.score !== undefined && (
              <span style={{ fontSize: 10, letterSpacing: '0.1em', color: theme.accent, border: `1px solid ${theme.line}`, padding: '2px 6px' }}>
                {Math.round(coach.score * 100)}% match
              </span>
            )}
          </div>
          <div style={{ fontSize: 12, color: theme.inkFaint, marginBottom: 4 }}>{coach.credential}</div>
          <div style={{ fontSize: 13, color: theme.inkSoft, fontStyle: 'italic', fontFamily: '"Fraunces", serif' }}>{coach.specialism}</div>
        </div>
      </div>
      <p style={{ fontSize: 14, color: theme.ink, lineHeight: 1.5, margin: 0, marginBottom: 12 }}>{coach.bio}</p>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <div style={{ fontSize: 13, color: theme.inkSoft }}><span style={{ fontFamily: '"Fraunces", serif', fontSize: 17, color: theme.ink }}>£{coach.rate || coach.rate_gbp || 0}</span> / session</div>
        {selected ? (
          <div style={{ background: theme.success, color: theme.paper, padding: '8px 14px', fontSize: 12, display: 'flex', alignItems: 'center', gap: 6 }}><Check size={13} /> Your coach</div>
        ) : (
          <button onClick={() => onSelect(coach)} style={{ background: theme.ink, color: theme.paper, border: 'none', padding: '8px 14px', fontSize: 13, cursor: 'pointer' }}>Choose coach</button>
        )}
      </div>
    </div>
  );
}

function SettingsView({ session, updateData, logout }) {
  const { data } = session;
  const persona = PERSONAS[data.profile?.personaId] || PERSONAS.mentor;
  const [showSecurity, setShowSecurity] = useState(false);
  const changePersona = (newId) => updateData({ ...data, profile: { ...data.profile, personaId: newId }, chat: [] });

  return (
    <div style={{ padding: '2rem 1.5rem', maxWidth: 600, margin: '0 auto' }}>
      <div style={{ fontSize: 11, letterSpacing: '0.25em', textTransform: 'uppercase', color: theme.inkFaint, marginBottom: 6 }}>Settings</div>
      <h1 style={{ fontFamily: '"Fraunces", serif', fontWeight: 400, fontSize: '2rem', color: theme.ink, margin: 0, marginBottom: '1.75rem', letterSpacing: '-0.02em' }}>Your setup</h1>

      <Section title="Account" icon={<User size={14} />}>
        <div style={{ background: theme.paper, border: `1px solid ${theme.line}`, padding: '14px', fontSize: 13, color: theme.inkSoft }}>
          <div>Logged in as <strong style={{ color: theme.ink }}>{session.email}</strong></div>
          <div style={{ fontSize: 11, marginTop: 6, color: theme.inkFaint }}>Account type: client</div>
        </div>
      </Section>

      <Section title="AI coach">
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {Object.values(PERSONAS).map(p => {
            const current = p.id === persona.id;
            return (
              <button key={p.id} onClick={() => changePersona(p.id)}
                style={{ background: current ? theme.paper : 'transparent', border: `1px solid ${current ? theme.ink : theme.line}`, padding: '12px 14px', cursor: 'pointer', textAlign: 'left', display: 'flex', alignItems: 'center', gap: 12 }}>
                <div style={{ fontSize: 24, color: p.accent, fontFamily: '"Fraunces", serif', lineHeight: 1 }}>{p.glyph}</div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontFamily: '"Fraunces", serif', fontSize: 15, color: theme.ink }}>{p.name}</div>
                  <div style={{ fontSize: 12, color: theme.inkSoft }}>{p.tagline}</div>
                </div>
                {current && <Check size={16} color={theme.ink} />}
              </button>
            );
          })}
        </div>
      </Section>

      <Section title="Security" icon={<Shield size={14} />}>
        <button onClick={() => setShowSecurity(!showSecurity)}
          style={{ width: '100%', background: theme.paper, border: `1px solid ${theme.line}`, padding: '12px 14px', fontSize: 13, color: theme.ink, cursor: 'pointer', textAlign: 'left', display: 'flex', alignItems: 'center', gap: 10 }}>
          <Lock size={14} color={theme.success} />
          <span style={{ flex: 1 }}>How your data is protected</span>
          <ChevronRight size={14} style={{ transform: showSecurity ? 'rotate(90deg)' : 'none', transition: 'transform 200ms' }} />
        </button>
        {showSecurity && (
          <div style={{ background: theme.paper, border: `1px solid ${theme.line}`, borderTop: 'none', padding: '14px', fontSize: 12, color: theme.inkSoft, lineHeight: 1.7 }}>
            <div style={{ marginBottom: 8 }}>◈ <strong style={{ color: theme.ink }}>Password:</strong> PBKDF2 with 210,000 iterations (OWASP 2024)</div>
            <div style={{ marginBottom: 8 }}>◈ <strong style={{ color: theme.ink }}>Your data:</strong> Encrypted locally with AES-GCM-256</div>
            <div style={{ marginBottom: 8 }}>◈ <strong style={{ color: theme.ink }}>Encryption key:</strong> Derived from your password, never stored</div>
            <div style={{ marginBottom: 8 }}>◈ <strong style={{ color: theme.ink }}>Network:</strong> Messages to AI sent over HTTPS, never stored by us</div>
            <div style={{ color: theme.inkFaint, marginTop: 12, fontSize: 11 }}>In production this would also include: server-side zero-knowledge backup, TLS-pinned API connections, and independent security audit.</div>
          </div>
        )}
      </Section>

      <Section title="Session">
        <button onClick={logout} style={{ background: 'transparent', border: `1px solid ${theme.danger}`, color: theme.danger, padding: '10px 14px', fontSize: 13, cursor: 'pointer', width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}>
          <LogOut size={14} /> Log out
        </button>
        <div style={{ fontSize: 11, color: theme.inkFaint, marginTop: 8, lineHeight: 1.5 }}>Logging out re-encrypts your data. You'll need your password to log back in.</div>
      </Section>
    </div>
  );
}

// ============================================================================
// COACH VIEWS
// ============================================================================

function CoachDashboard({ session, updateData, onOpenClient }) {
  const coach = session.data.coach;
  const [myClients, setMyClients] = useState([]);
  const [pendingBriefs, setPendingBriefs] = useState(0);

  useEffect(() => {
    coachesApi.getBriefs().then(d => setPendingBriefs((d.briefs || []).length)).catch(() => {});
    coachesApi.getMyClients().then(d => setMyClients(d.clients || [])).catch(() => {});
  }, []);

  return (
    <div style={{ padding: '2rem 1.5rem', maxWidth: 600, margin: '0 auto' }}>
      <div style={{ marginBottom: '1.5rem' }}>
        <div style={{ fontSize: 11, letterSpacing: '0.25em', textTransform: 'uppercase', color: theme.inkFaint, marginBottom: 6 }}>
          {new Date().toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long' })}
        </div>
        <h1 style={{ fontFamily: '"Fraunces", serif', fontWeight: 400, fontSize: '2.25rem', color: theme.ink, margin: 0, letterSpacing: '-0.02em', lineHeight: 1.1 }}>
          Morning, <em style={{ fontStyle: 'italic' }}>{coach?.name?.split(' ')[0] || 'Coach'}</em>.
        </h1>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 8, marginBottom: '1.5rem' }}>
        <StatCard label="Active clients" value={myClients.length} icon={<Users size={14} />} />
        <StatCard label="Open briefs" value={pendingBriefs} icon={<Inbox size={14} />} accent={pendingBriefs > 0 ? theme.accent : undefined} />
        <StatCard label="Sessions" value={0} icon={<Calendar size={14} />} />
      </div>

      <InfoCallout icon={<EyeOff size={14} />} tone="info">
        You see <strong>engagement signals</strong> from clients — completion, consistency, recurring themes. You don't see the private content of check-ins unless clients share it with you ahead of a session.
      </InfoCallout>

      {pendingBriefs > 0 && (
        <InfoCallout icon={<Inbox size={14} />} tone="accent">
          <strong>{pendingBriefs} client brief{pendingBriefs === 1 ? '' : 's'} available.</strong> These are clients who didn't get a strong auto-match. Review their anonymised profiles to see if any fit your practice.
        </InfoCallout>
      )}

      <Section title="Your clients" icon={<Users size={14} />}>
        {myClients.length === 0 ? (
          <EmptyState text="No clients yet. Check the Briefs tab to find new ones, or build your profile so clients can find you." />
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {myClients.map(c => (
              <button key={c.id} onClick={() => onOpenClient(c.id)}
                style={{ background: theme.paper, border: `1px solid ${theme.line}`, borderLeft: `3px solid ${theme.accent}`, padding: '14px', textAlign: 'left', cursor: 'pointer', width: '100%', display: 'flex', alignItems: 'center', gap: 12 }}>
                <div style={{ width: 38, height: 38, borderRadius: '50%', background: theme.bgDeep, display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: '"Fraunces", serif', fontSize: 15, color: theme.accent, flexShrink: 0 }}>
                  {(c.name || 'C').split(' ').map(n => n[0]).join('')}
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontFamily: '"Fraunces", serif', fontSize: 15, color: theme.ink }}>{c.name}</div>
                  <div style={{ fontSize: 11, color: theme.inkFaint }}>Active since {new Date(c.linkedAt).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}</div>
                </div>
                <ChevronRight size={14} color={theme.inkFaint} />
              </button>
            ))}
          </div>
        )}
      </Section>

      <Section title="Your matching profile" icon={<Target size={14} />}
        action={<HelpTip title="Style matching">Clients answer the same four questions during signup. We compare your profile to theirs to surface the best matches on both sides.</HelpTip>}>
        <div style={{ background: theme.paper, border: `1px solid ${theme.line}`, padding: '14px', fontSize: 13, color: theme.inkSoft, lineHeight: 1.7 }}>
          <AxesBar label="Directness" value={coach.axes.directness} />
          <AxesBar label="Warmth" value={coach.axes.warmth} />
          <AxesBar label="Structure" value={coach.axes.structure} />
          <AxesBar label="Pace" value={coach.axes.energy} />
          <div style={{ fontSize: 11, color: theme.inkFaint, marginTop: 10 }}>Clients with matching styles see you as an excellent match. Stretch-match clients may also find you.</div>
        </div>
      </Section>
    </div>
  );
}

function StatCard({ label, value, icon, accent }) {
  return (
    <div style={{ background: theme.paper, border: `1px solid ${theme.line}`, padding: '12px' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 4, marginBottom: 4, color: accent || theme.inkSoft }}>{icon}<span style={{ fontSize: 10, letterSpacing: '0.1em', textTransform: 'uppercase' }}>{label}</span></div>
      <div style={{ fontFamily: '"Fraunces", serif', fontSize: 28, color: accent || theme.ink, lineHeight: 1 }}>{value}</div>
    </div>
  );
}

function AxesBar({ label, value }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6 }}>
      <div style={{ width: 80, fontSize: 12, color: theme.ink }}>{label}</div>
      <div style={{ flex: 1, height: 4, background: theme.bgDeep, position: 'relative' }}>
        <div style={{ position: 'absolute', left: 0, top: 0, height: '100%', width: `${value * 100}%`, background: theme.accent }} />
      </div>
      <div style={{ fontSize: 11, color: theme.inkFaint, width: 32, textAlign: 'right' }}>{Math.round(value * 100)}%</div>
    </div>
  );
}

// Build a client signal view from their (encrypted, unreadable) profile
// For the demo, we use synthesised data since we can't actually decrypt another user's vault
function buildClientSignal(email, store) {
  // In real app: coach has specific read tokens for signals only
  // For demo, we use the linked client's pseudo-data
  return {
    email,
    name: email.split('@')[0].split('.').map(s => s[0].toUpperCase() + s.slice(1)).join(' '),
    startedMonth: 'Recently',
    engagementScore: 0.7,
    weeklyCheckIns: 9,
    habitConsistency: 78,
    last7Days: Array.from({ length: 7 }, () => ({ morning: Math.random() > 0.3, evening: Math.random() > 0.4 })),
    themes: ['Building consistency', 'Work focus'],
    flaggedSupport: null,
    nextSession: null,
  };
}

function ClientSignalCard({ client, onClick, coachEmail }) {
  const statusColor = client.engagementScore > 0.7 ? theme.success : client.engagementScore > 0.4 ? theme.accent : theme.danger;
  const statusLabel = client.engagementScore > 0.7 ? 'On track' : client.engagementScore > 0.4 ? 'Mixed week' : 'Slipping';

  // Unread message count for this client
  let unread = 0;
  if (coachEmail) {
    // unread loaded async in useEffect below
    unread = 0; // updated via useEffect
  }

  const Wrapper = onClick ? 'button' : 'div';
  return (
    <Wrapper onClick={onClick}
      style={{ background: theme.paper, border: `1px solid ${theme.line}`, borderLeft: `3px solid ${statusColor}`, padding: '14px', width: '100%', textAlign: 'left', cursor: onClick ? 'pointer' : 'default', display: 'block', fontFamily: 'inherit' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 12 }}>
        <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
          <div style={{ width: 40, height: 40, borderRadius: '50%', background: theme.bgDeep, display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: '"Fraunces", serif', fontSize: 14, color: theme.accent, position: 'relative' }}>
            {client.name.split(' ').map(n => n[0]).join('')}
            {unread > 0 && (
              <span style={{ position: 'absolute', top: -4, right: -4, background: theme.danger, color: theme.paper, fontSize: 9, fontWeight: 600, borderRadius: 10, minWidth: 16, height: 16, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '0 4px' }}>
                {unread}
              </span>
            )}
          </div>
          <div>
            <div style={{ fontFamily: '"Fraunces", serif', fontSize: 16, color: theme.ink }}>{client.name}</div>
            <div style={{ fontSize: 11, color: theme.inkFaint }}>Since {client.startedMonth}</div>
          </div>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 4 }}>
          <div style={{ fontSize: 11, color: statusColor, letterSpacing: '0.1em', textTransform: 'uppercase', fontWeight: 600 }}>{statusLabel}</div>
          {onClick && <ChevronRight size={14} color={theme.inkFaint} />}
        </div>
      </div>
      <div style={{ marginBottom: 12 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
          <div style={{ fontSize: 10, letterSpacing: '0.1em', textTransform: 'uppercase', color: theme.inkFaint }}>Last 7 days</div>
          <div style={{ fontSize: 11, color: theme.inkSoft }}>{client.weeklyCheckIns}/14 check-ins</div>
        </div>
        <div style={{ display: 'flex', gap: 3, height: 24 }}>
          {client.last7Days.map((d, i) => (
            <div key={i} style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
              <div style={{ flex: 1, background: d.morning ? theme.success : theme.line, opacity: d.morning ? 0.8 : 0.5 }} />
              <div style={{ flex: 1, background: d.evening ? theme.accent : theme.line, opacity: d.evening ? 0.8 : 0.5 }} />
            </div>
          ))}
        </div>
        <div style={{ marginTop: 4, fontSize: 9, color: theme.inkFaint, letterSpacing: '0.05em', textAlign: 'right' }}>{client.habitConsistency}% habit consistency</div>
      </div>
      <div>
        <div style={{ fontSize: 10, letterSpacing: '0.1em', textTransform: 'uppercase', color: theme.inkFaint, marginBottom: 6 }}>Recent themes</div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
          {client.themes.map(t => (<span key={t} style={{ background: theme.bgDeep, padding: '4px 10px', fontSize: 12, color: theme.accentDeep, borderRadius: 999 }}>{t}</span>))}
        </div>
      </div>
    </Wrapper>
  );
}

function CoachClientsView({ session, onOpenClient }) {
  const [myClients, setMyClients] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    coachesApi.getMyClients()
      .then(d => setMyClients(d.clients || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  return (
    <div style={{ padding: '2rem 1.5rem', maxWidth: 600, margin: '0 auto' }}>
      <div style={{ fontSize: 11, letterSpacing: '0.25em', textTransform: 'uppercase', color: theme.inkFaint, marginBottom: 6 }}>Your practice</div>
      <h1 style={{ fontFamily: '"Fraunces", serif', fontWeight: 400, fontSize: '2rem', color: theme.ink, margin: 0, marginBottom: '1rem', letterSpacing: '-0.02em' }}>Clients</h1>
      <p style={{ fontSize: 13, color: theme.inkSoft, marginBottom: 20, lineHeight: 1.6 }}>Tap a client to see their engagement signals, send a nudge, or generate a pre-session brief.</p>

      <InfoCallout icon={<EyeOff size={14} />} tone="info">
        You see <strong>patterns and signals</strong>, never raw content. Clients decide what to share before sessions through their own app.
      </InfoCallout>

      {loading ? (
        <div style={{ textAlign: 'center', padding: '32px', color: theme.inkFaint, fontSize: 13 }}>Loading clients...</div>
      ) : myClients.length === 0 ? (
        <EmptyState text="No active clients yet. Accept briefs to build your practice." />
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {myClients.map(c => (
            <button key={c.id} onClick={() => onOpenClient(c.id)}
              style={{ background: theme.paper, border: `1px solid ${theme.line}`, borderLeft: `3px solid ${theme.accent}`, padding: '16px', textAlign: 'left', cursor: 'pointer', width: '100%' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 8 }}>
                <div style={{ width: 40, height: 40, borderRadius: '50%', background: theme.bgDeep, display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: '"Fraunces", serif', fontSize: 16, color: theme.accent, flexShrink: 0 }}>
                  {(c.name || 'C').split(' ').map(n => n[0]).join('')}
                </div>
                <div>
                  <div style={{ fontFamily: '"Fraunces", serif', fontSize: 16, color: theme.ink }}>{c.name}</div>
                  <div style={{ fontSize: 11, color: theme.inkFaint }}>Active since {new Date(c.linkedAt).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}</div>
                </div>
                <ChevronRight size={16} color={theme.inkFaint} style={{ marginLeft: 'auto' }} />
              </div>
              {c.goals?.length > 0 && (
                <div style={{ fontSize: 12, color: theme.inkSoft }}>
                  Focus: {c.goals.slice(0, 2).join(', ')}
                </div>
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function CoachBriefsView({ session, updateData }) {
  const [briefs, setBriefs] = useState([]);
  useEffect(() => {
    coachesApi.getBriefs().then(d => setBriefs(d.briefs || [])).catch(() => {});
  }, []);

  const goalLabels = {
    health: 'Health & energy', mind: 'Mental wellbeing', work: 'Career', focus: 'Focus & productivity',
    relationships: 'Relationships', money: 'Finances', purpose: 'Purpose', habits: 'Daily habits',
  };

  const acceptBrief = async (brief) => {
    if (!confirmAction('Accept this client? They will be notified and you will be connected.')) return;
    try {
      await coachesApi.acceptBrief(brief.briefId);
      setBriefs(prev => prev.filter(b => b.briefId !== brief.briefId));
      window.alert('Brief accepted. The client will be notified.');
    } catch (e) {
      window.alert('Could not accept brief: ' + e.message);
    }
  };

  return (
    <div style={{ padding: '2rem 1.5rem', maxWidth: 600, margin: '0 auto' }}>
      <div style={{ fontSize: 11, letterSpacing: '0.25em', textTransform: 'uppercase', color: theme.inkFaint, marginBottom: 6 }}>Opportunity</div>
      <h1 style={{ fontFamily: '"Fraunces", serif', fontWeight: 400, fontSize: '2rem', color: theme.ink, margin: 0, marginBottom: '0.75rem', letterSpacing: '-0.02em' }}>Client briefs</h1>
      <p style={{ fontSize: 14, color: theme.inkSoft, lineHeight: 1.6, marginBottom: '1.5rem' }}>Clients who didn't auto-match well share an anonymised brief of how they want to be coached. Review and opt in if it suits your practice.</p>

      <InfoCallout icon={<Info size={14} />} tone="info">
        <strong>Why briefs exist.</strong> A client wanting gentle, reflective coaching won't match with only direct, challenging coaches on the platform. Rather than give them a poor match, we surface their anonymised needs here. You learn what clients are looking for, they find the right coach.
      </InfoCallout>

      {briefs.length === 0 ? (
        <EmptyState text="No open briefs right now. Check back regularly." />
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {briefs.map(brief => {
            const mc = session.data.coach ? matchScore(session.data.coach.axes, brief.axes) : 0;
            const quality = matchQuality(mc);
            const qualityColor = { excellent: theme.success, good: theme.accent, stretch: theme.info, poor: theme.inkFaint }[quality];
            return (
              <div key={brief.briefId} style={{ background: theme.paper, border: `1px solid ${theme.line}`, borderLeft: `3px solid ${qualityColor}`, padding: '16px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 12 }}>
                  <div>
                    <div style={{ fontFamily: '"Fraunces", serif', fontSize: 17, color: theme.ink, marginBottom: 2 }}>Anonymous client</div>
                    <div style={{ fontSize: 11, color: theme.inkFaint, letterSpacing: '0.05em' }}>Posted {new Date(brief.createdAt).toLocaleDateString('en-GB')}</div>
                  </div>
                  <span style={{ fontSize: 10, letterSpacing: '0.1em', textTransform: 'uppercase', color: qualityColor, border: `1px solid ${qualityColor}`, padding: '3px 8px', fontWeight: 600 }}>
                    {Math.round(mc * 100)}% — {quality}
                  </span>
                </div>

                <div style={{ marginBottom: 12 }}>
                  <div style={{ fontSize: 10, letterSpacing: '0.1em', textTransform: 'uppercase', color: theme.inkFaint, marginBottom: 6 }}>Focus areas</div>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                    {brief.goals.map(g => (<span key={g} style={{ background: theme.bgDeep, padding: '4px 10px', fontSize: 12, color: theme.accentDeep, borderRadius: 999 }}>{goalLabels[g] || g}</span>))}
                  </div>
                </div>

                <div style={{ marginBottom: 12 }}>
                  <div style={{ fontSize: 10, letterSpacing: '0.1em', textTransform: 'uppercase', color: theme.inkFaint, marginBottom: 8 }}>How they want to be coached</div>
                  <StyleCompare client={brief.axes} coach={session.data.coach?.axes} />
                </div>

                <div style={{ marginBottom: 12, fontSize: 12, color: theme.inkSoft }}>
                  Chose <strong style={{ color: theme.ink }}>{PERSONAS[brief.personaChoice]?.name}</strong> as their AI persona.
                </div>

                <div style={{ display: 'flex', gap: 8, borderTop: `1px solid ${theme.line}`, paddingTop: 12 }}>
                  <button style={{ flex: 1, background: 'transparent', border: `1px solid ${theme.line}`, color: theme.inkSoft, padding: '8px 12px', fontSize: 12, cursor: 'pointer' }}>Pass</button>
                  <button onClick={() => acceptBrief(brief)} style={{ flex: 1, background: theme.ink, color: theme.paper, border: 'none', padding: '8px 12px', fontSize: 12, cursor: 'pointer' }}>Accept brief</button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function StyleCompare({ client, coach }) {
  const axes = [
    { key: 'directness', label: 'Directness' },
    { key: 'warmth', label: 'Warmth' },
    { key: 'structure', label: 'Structure' },
    { key: 'energy', label: 'Pace' },
  ];
  return (
    <div style={{ background: theme.bg, padding: '10px 12px', border: `1px solid ${theme.line}`, fontSize: 11 }}>
      {axes.map(a => {
        const cVal = client[a.key];
        const coachVal = coach ? coach[a.key] : null;
        return (
          <div key={a.key} style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6 }}>
            <div style={{ width: 70, fontSize: 11, color: theme.inkSoft }}>{a.label}</div>
            <div style={{ flex: 1, height: 6, background: theme.paper, border: `1px solid ${theme.line}`, position: 'relative' }}>
              {/* Client marker */}
              <div style={{ position: 'absolute', left: `calc(${cVal * 100}% - 5px)`, top: -3, width: 10, height: 10, background: theme.accent, borderRadius: '50%', border: `1.5px solid ${theme.paper}` }} title="Client wants" />
              {/* Coach marker */}
              {coach && (<div style={{ position: 'absolute', left: `calc(${coachVal * 100}% - 4px)`, top: -1, width: 8, height: 8, background: theme.ink, borderRadius: '50%', border: `1px solid ${theme.paper}` }} title="You" />)}
            </div>
          </div>
        );
      })}
      <div style={{ display: 'flex', gap: 12, marginTop: 8, fontSize: 10, color: theme.inkFaint }}>
        <span><span style={{ display: 'inline-block', width: 8, height: 8, background: theme.accent, borderRadius: '50%', marginRight: 4 }} />Client</span>
        <span><span style={{ display: 'inline-block', width: 8, height: 8, background: theme.ink, borderRadius: '50%', marginRight: 4 }} />You</span>
      </div>
    </div>
  );
}

// ============================================================================
// COACH CLIENT DETAIL VIEW — pre-session brief + nudges + session notes
// ============================================================================

function CoachClientDetailView({ session, clientId, onBack }) {
  const [view, setView] = useState('overview');
  const [client, setClient] = useState(null);
  const [sharedContext, setSharedContextState] = useState(null);
  const [linkId, setLinkId] = useState(null);

  // Fetch linkId first
  useEffect(() => {
    coachesApi.getLinkId(clientId)
      .then(d => { if (d.linkId) setLinkId(d.linkId); })
      .catch(() => {});
  }, [clientId]);

  // Load shared context once linkId is available
  useEffect(() => {
    if (!linkId) return;
    markMessagesRead(linkId, null, session.id).catch(() => {});
    coachesApi.getSharedContext(linkId).then(d => setSharedContextState(d.sharedContext)).catch(() => {});
  }, [linkId]);

  // Load client signals
  useEffect(() => {
    signalsApi.getForClient(clientId, 14)
      .then(d => {
        const rows = d.signals || [];
        const last7 = rows.slice(0, 7).reverse();
        const weekDone = rows.filter(r => r.morning_done || r.evening_done).length;
        const habitPcts = rows.filter(r => r.habits_total > 0).map(r => r.habits_done / r.habits_total);
        const avgConsistency = habitPcts.length > 0 ? Math.round(habitPcts.reduce((a,b) => a+b, 0) / habitPcts.length * 100) : 0;
        const allThemes = rows.flatMap(r => r.themes || []);
        const themeCounts = allThemes.reduce((acc, t) => { acc[t] = (acc[t]||0)+1; return acc; }, {});
        const topThemes = Object.entries(themeCounts).sort((a,b) => b[1]-a[1]).slice(0,3).map(([t]) => t);
        setClient({
          id: clientId,
          name: 'Client', // will be updated below
          startedMonth: 'Recently',
          engagementScore: Math.min(weekDone / 14, 1),
          weeklyCheckIns: weekDone,
          habitConsistency: avgConsistency,
          last7Days: last7.map(r => ({ morning: r.morning_done, evening: r.evening_done })),
          themes: topThemes.length > 0 ? topThemes : ['Building consistency'],
          flaggedSupport: null,
        });
      })
      .catch(() => {
        setClient({
          id: clientId,
          name: 'Client',
          startedMonth: 'Recently',
          engagementScore: 0.5,
          weeklyCheckIns: 0,
          habitConsistency: 0,
          last7Days: Array(7).fill({ morning: false, evening: false }),
          themes: ['No data yet'],
          flaggedSupport: null,
        });
      });

    // Fetch client name from my-clients list
    coachesApi.getMyClients()
      .then(d => {
        const c = (d.clients || []).find(c => c.id === clientId);
        if (c) setClient(prev => prev ? { ...prev, name: c.name } : null);
      })
      .catch(() => {});
  }, [clientId]);

  if (!client) return null;

  return (
    <div style={{ maxWidth: 600, margin: '0 auto' }}>
      <div style={{ padding: '1.25rem 1.5rem', borderBottom: `1px solid ${theme.line}`, background: theme.bg, position: 'sticky', top: 0, zIndex: 10 }}>
        <button onClick={onBack} style={{ background: 'transparent', border: 'none', color: theme.inkSoft, fontSize: 14, cursor: 'pointer', padding: 0, marginBottom: 10, display: 'flex', alignItems: 'center', gap: 6 }}>
          ← Back to clients
        </button>
        <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
          <div style={{ width: 44, height: 44, borderRadius: '50%', background: theme.bgDeep, display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: '"Fraunces", serif', fontSize: 16, color: theme.accent }}>
            {client.name.split(' ').map(n => n[0]).join('')}
          </div>
          <div style={{ flex: 1 }}>
            <div style={{ fontFamily: '"Fraunces", serif', fontSize: 20, color: theme.ink, lineHeight: 1.2 }}>{client.name}</div>
            <div style={{ fontSize: 12, color: theme.inkFaint }}>Client since {client.startedMonth}</div>
          </div>
        </div>

        <div style={{ display: 'flex', gap: 4, marginTop: 14, borderBottom: `1px solid ${theme.line}`, marginBottom: -21 }}>
          {[
            { id: 'overview', label: 'Signals', icon: TrendingUp },
            { id: 'messages', label: 'Nudges', icon: MessageSquare },
            { id: 'brief', label: 'Pre-session', icon: FileText },
            { id: 'notes', label: 'Notes', icon: BookOpen },
          ].map(t => {
            const active = view === t.id;
            const Icon = t.icon;
            return (
              <button key={t.id} onClick={() => setView(t.id)}
                style={{ background: 'transparent', border: 'none', padding: '10px 6px', fontSize: 12, cursor: 'pointer', color: active ? theme.ink : theme.inkFaint, borderBottom: active ? `2px solid ${theme.accent}` : '2px solid transparent', display: 'flex', alignItems: 'center', gap: 6, fontWeight: active ? 600 : 400 }}>
                <Icon size={13} /> {t.label}
              </button>
            );
          })}
        </div>
      </div>

      <div style={{ padding: '1.5rem' }}>
        {view === 'overview' && <ClientOverviewPane client={client} sharedContext={sharedContext} />}
        {view === 'messages' && <CoachMessagesPane session={session} linkId={linkId} clientEmail={clientId} clientName={client.name} />}
        {view === 'brief' && <PreSessionBriefPane session={session} clientEmail={clientId} client={client} sharedContext={sharedContext} />}
        {view === 'notes' && <SessionNotesPane session={session} linkId={linkId} clientEmail={clientId} clientName={client.name} />}
      </div>
    </div>
  );
}

function ClientOverviewPane({ client, sharedContext }) {
  return (
    <div>
      <InfoCallout icon={<EyeOff size={14} />} tone="info">
        Signals show engagement patterns. Raw check-in content stays with the client unless they share it before a session.
      </InfoCallout>

      <Section title="Engagement" icon={<TrendingUp size={14} />}>
        <div style={{ background: theme.paper, border: `1px solid ${theme.line}`, padding: '14px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 10 }}>
            <div style={{ fontSize: 10, letterSpacing: '0.1em', textTransform: 'uppercase', color: theme.inkFaint }}>Last 7 days</div>
            <div style={{ fontSize: 11, color: theme.inkSoft }}>{client.weeklyCheckIns}/14 check-ins</div>
          </div>
          <div style={{ display: 'flex', gap: 3, height: 32, marginBottom: 10 }}>
            {client.last7Days.map((d, i) => (
              <div key={i} style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
                <div style={{ flex: 1, background: d.morning ? theme.success : theme.line, opacity: d.morning ? 0.8 : 0.5 }} title="Morning" />
                <div style={{ flex: 1, background: d.evening ? theme.accent : theme.line, opacity: d.evening ? 0.8 : 0.5 }} title="Evening" />
              </div>
            ))}
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, color: theme.inkFaint }}>
            <span>Habit consistency: <strong style={{ color: theme.ink }}>{client.habitConsistency}%</strong></span>
            <span>Streak: <strong style={{ color: theme.ink }}>—</strong></span>
          </div>
        </div>
      </Section>

      <Section title="Recurring themes" icon={<Target size={14} />}
        action={<HelpTip title="Themes">Detected patterns from how frequently certain topics appear in the client's check-ins. Not the content itself.</HelpTip>}>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
          {client.themes.map(t => (<span key={t} style={{ background: theme.paper, border: `1px solid ${theme.line}`, padding: '6px 12px', fontSize: 13, color: theme.ink, borderRadius: 999 }}>{t}</span>))}
        </div>
      </Section>

      {sharedContext ? (
        <Section title="Shared for next session" icon={<Share2 size={14} />}>
          <div style={{ background: theme.paper, border: `1px solid ${theme.success}`, borderLeft: `3px solid ${theme.success}`, padding: '14px' }}>
            <div style={{ fontSize: 11, color: theme.success, letterSpacing: '0.1em', textTransform: 'uppercase', fontWeight: 600, marginBottom: 8 }}>Shared {timeAgo(sharedContext.sharedAt)}</div>
            {sharedContext.sessionAgenda && (
              <div style={{ marginBottom: 10 }}>
                <div style={{ fontSize: 11, color: theme.inkFaint, letterSpacing: '0.05em', textTransform: 'uppercase', marginBottom: 4 }}>Wants to discuss</div>
                <div style={{ fontFamily: '"Fraunces", serif', fontSize: 15, color: theme.ink, lineHeight: 1.5 }}>{sharedContext.sessionAgenda}</div>
              </div>
            )}
            {sharedContext.flaggedTopics?.length > 0 && (
              <div>
                <div style={{ fontSize: 11, color: theme.inkFaint, letterSpacing: '0.05em', textTransform: 'uppercase', marginBottom: 4 }}>Flagged topics</div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                  {sharedContext.flaggedTopics.map(t => (<span key={t} style={{ background: theme.bgDeep, padding: '3px 9px', fontSize: 12, color: theme.ink, borderRadius: 999 }}>{t}</span>))}
                </div>
              </div>
            )}
          </div>
        </Section>
      ) : (
        <Section title="Shared for next session" icon={<Share2 size={14} />}>
          <EmptyState text="Client hasn't yet shared anything specific for the upcoming session." />
        </Section>
      )}
    </div>
  );
}

function CoachMessagesPane({ session, linkId, clientEmail, clientName }) {
  const [msgs, setMsgs] = useState([]);
  useEffect(() => {
    if (linkId) getMessages(linkId, null).then(setMsgs).catch(() => {});
  }, [linkId]);
  const [draft, setDraft] = useState('');
  const [kind, setKind] = useState('nudge');
  const scrollRef = useRef(null);

  useEffect(() => { if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight; }, [msgs]);

  const send = async () => {
    if (!draft.trim() || !linkId) return;
    try {
      const result = await messagesApi.send(linkId, {
        body: draft.trim(),
        kind,
        recipientId: clientEmail,
      });
      if (result.message) setMsgs(prev => [...prev, result.message]);
    } catch (e) { console.error('Send error:', e.message); }
    setDraft('');
  };

  const kindOptions = [
    { id: 'nudge', label: 'Nudge', desc: 'A small prompt or check-in' },
    { id: 'question', label: 'Question', desc: 'Something to reflect on' },
    { id: 'encouragement', label: 'Encouragement', desc: 'Acknowledge progress' },
  ];

  const templates = {
    nudge: [`I noticed a quieter week on check-ins. How are you arriving today?`, `Small reminder: the priority we agreed on last time — how's it landing?`, `Just checking in. One small thing between now and our next session?`],
    question: [`What's one thing you've learned about yourself this week?`, `Where did you show up well this week? Where didn't you?`, `If next week could only contain one priority, what would it be?`],
    encouragement: [`Strong consistency this week — good to see you showing up for yourself.`, `Your check-ins this week show real thoughtfulness. Keep at it.`, `The work you did between sessions is visible. Well done.`],
  };

  return (
    <div>
      <InfoCallout icon={<Info size={14} />} tone="info">
        <strong>Nudges</strong> are short, between-session messages. They're not therapy or crisis support — use them for accountability, encouragement, or a small question to reflect on. Keep them brief.
      </InfoCallout>

      <div ref={scrollRef} style={{ background: theme.paper, border: `1px solid ${theme.line}`, padding: '12px', maxHeight: 340, overflowY: 'auto', marginBottom: 16 }}>
        {msgs.length === 0 ? (
          <div style={{ fontSize: 13, color: theme.inkFaint, textAlign: 'center', padding: '20px 10px' }}>No messages yet. Send a nudge below to start.</div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {msgs.map(m => {
              const fromCoach = m.from === session.id;
              return (
                <div key={m.id} style={{ display: 'flex', justifyContent: fromCoach ? 'flex-end' : 'flex-start' }}>
                  <div style={{ maxWidth: '82%' }}>
                    {m.kind && fromCoach && (<div style={{ fontSize: 9, letterSpacing: '0.15em', textTransform: 'uppercase', color: theme.inkFaint, marginBottom: 3, textAlign: 'right' }}>{m.kind}</div>)}
                    <div style={{ background: fromCoach ? theme.ink : theme.bgDeep, color: fromCoach ? theme.paper : theme.ink, padding: '10px 12px', fontSize: 14, lineHeight: 1.5, whiteSpace: 'pre-wrap', fontFamily: fromCoach ? 'Inter, sans-serif' : '"Fraunces", serif' }}>{m.body}</div>
                    <div style={{ fontSize: 10, color: theme.inkFaint, marginTop: 3, textAlign: fromCoach ? 'right' : 'left' }}>{timeAgo(m.createdAt)}{fromCoach && m.read ? ' · read' : ''}</div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      <div style={{ marginBottom: 10 }}>
        <div style={{ fontSize: 11, letterSpacing: '0.1em', textTransform: 'uppercase', color: theme.inkFaint, marginBottom: 6 }}>Type of message</div>
        <div style={{ display: 'flex', gap: 6 }}>
          {kindOptions.map(k => (
            <button key={k.id} onClick={() => setKind(k.id)}
              style={{ flex: 1, background: kind === k.id ? theme.ink : theme.paper, color: kind === k.id ? theme.paper : theme.ink, border: `1px solid ${kind === k.id ? theme.ink : theme.line}`, padding: '8px 10px', fontSize: 12, cursor: 'pointer' }}>
              {k.label}
            </button>
          ))}
        </div>
        <div style={{ fontSize: 11, color: theme.inkFaint, marginTop: 4 }}>{kindOptions.find(k => k.id === kind)?.desc}</div>
      </div>

      <div style={{ marginBottom: 10 }}>
        <div style={{ fontSize: 11, letterSpacing: '0.1em', textTransform: 'uppercase', color: theme.inkFaint, marginBottom: 6 }}>Starter templates</div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          {templates[kind].map((t, i) => (
            <button key={i} onClick={() => setDraft(t)}
              style={{ background: 'transparent', border: `1px solid ${theme.line}`, padding: '8px 10px', fontSize: 12, color: theme.inkSoft, cursor: 'pointer', textAlign: 'left', fontStyle: 'italic', fontFamily: '"Fraunces", serif' }}>
              "{t}"
            </button>
          ))}
        </div>
      </div>

      <DictableText value={draft} onChange={setDraft} placeholder={`Write a ${kind} to ${clientName.split(' ')[0]}...`} rows={3} />

      <button onClick={send} disabled={!draft.trim()}
        style={{ width: '100%', background: draft.trim() ? theme.ink : theme.line, color: theme.paper, border: 'none', padding: '12px', fontSize: 14, cursor: draft.trim() ? 'pointer' : 'not-allowed', marginTop: 12, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}>
        <Send size={14} /> Send {kind}
      </button>
    </div>
  );
}

function PreSessionBriefPane({ session, clientEmail, client, sharedContext }) {
  const [brief, setBrief] = useState(null);
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState('');

  const generateBrief = async () => {
    setGenerating(true); setError('');
    try {
      const result = await aiApi.preSessionBrief({
        clientId: clientEmail, // server uses this to verify the link
        signals: {
          engagementScore: client.engagementScore,
          weeklyCheckIns: client.weeklyCheckIns,
          habitConsistency: client.habitConsistency,
          themes: client.themes,
        },
        sharedContext: sharedContext ? {
          agenda: sharedContext.sessionAgenda,
          flaggedTopics: sharedContext.flaggedTopics,
        } : null,
      });
      setBrief({ content: result.brief, generatedAt: result.generatedAt });
    } catch (e) {
      setError('Could not generate brief. Check your connection and try again.');
    } finally {
      setGenerating(false);
    }
  };

  return (
    <div>
      <InfoCallout icon={<FileText size={14} />} tone="accent">
        <strong>Pre-session brief.</strong> Generated on demand from engagement signals, recent nudges, previous session notes, and anything the client has chosen to share. Designed to be read in under two minutes before a session.
      </InfoCallout>

      {!brief && !generating && (
        <div style={{ background: theme.paper, border: `1px dashed ${theme.line}`, padding: '24px 16px', textAlign: 'center', marginBottom: 16 }}>
          <FileText size={28} color={theme.accent} style={{ margin: '0 auto 10px' }} />
          <div style={{ fontFamily: '"Fraunces", serif', fontSize: 16, color: theme.ink, marginBottom: 6 }}>Generate a pre-session brief</div>
          <div style={{ fontSize: 12, color: theme.inkSoft, marginBottom: 14, lineHeight: 1.5 }}>
            AI synthesises signals and context into a short, scannable summary. Fresh each time — never stored.
          </div>
          <button onClick={generateBrief}
            style={{ background: theme.ink, color: theme.paper, border: 'none', padding: '10px 16px', fontSize: 13, cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 8 }}>
            <Sparkles size={13} /> Generate brief
          </button>
        </div>
      )}

      {generating && (
        <div style={{ background: theme.paper, border: `1px solid ${theme.line}`, borderLeft: `3px solid ${theme.accent}`, padding: '20px', marginBottom: 16, display: 'flex', alignItems: 'center', gap: 12 }}>
          <Sparkles size={18} color={theme.accent} />
          <div style={{ fontSize: 13, color: theme.inkSoft, fontStyle: 'italic' }}>Synthesising signals and context for {client.name.split(' ')[0]}...</div>
        </div>
      )}

      {error && (
        <InfoCallout icon={<AlertTriangle size={14} />} tone="accent">{error}</InfoCallout>
      )}

      {brief && (
        <>
          <div style={{ background: theme.paper, border: `1px solid ${theme.line}`, borderLeft: `3px solid ${theme.accent}`, padding: '20px 22px', marginBottom: 12 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14, paddingBottom: 10, borderBottom: `1px solid ${theme.line}` }}>
              <div>
                <div style={{ fontSize: 10, letterSpacing: '0.2em', textTransform: 'uppercase', color: theme.accent, fontWeight: 600 }}>Pre-session brief</div>
                <div style={{ fontFamily: '"Fraunces", serif', fontSize: 18, color: theme.ink, marginTop: 2 }}>{client.name}</div>
              </div>
              <div style={{ fontSize: 11, color: theme.inkFaint, textAlign: 'right' }}>
                Generated<br />{timeAgo(brief.generatedAt)}
              </div>
            </div>
            <div style={{ fontFamily: '"Fraunces", serif', fontSize: 15, color: theme.ink, lineHeight: 1.65, whiteSpace: 'pre-wrap' }}>
              {brief.content}
            </div>
          </div>
          <button onClick={generateBrief}
            style={{ width: '100%', background: 'transparent', border: `1px solid ${theme.line}`, color: theme.inkSoft, padding: '10px', fontSize: 12, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}>
            <RefreshCw size={12} /> Regenerate
          </button>
        </>
      )}
    </div>
  );
}

function SessionNotesPane({ session, linkId, clientEmail, clientName }) {
  const [notes, setNotes] = useState([]);
  useEffect(() => {
    if (linkId) coachesApi.getSessionNotes(linkId).then(d => setNotes(d.notes || [])).catch(() => {});
  }, [linkId]);
  const [composing, setComposing] = useState(false);
  const [draft, setDraft] = useState('');

  const save = async () => {
    if (!draft.trim() || !linkId) return;
    try {
      const result = await coachesApi.saveSessionNote(linkId, draft.trim());
      if (result.note) setNotes(prev => [...prev, result.note]);
    } catch (e) { console.error('Note save error:', e.message); }
    setDraft(''); setComposing(false);
  };

  return (
    <div>
      <InfoCallout icon={<BookOpen size={14} />} tone="info">
        Your private notes from sessions. Only visible to you. They inform future pre-session briefs but are never shared with the client.
      </InfoCallout>

      {!composing && (
        <button onClick={() => setComposing(true)}
          style={{ width: '100%', background: theme.ink, color: theme.paper, border: 'none', padding: '11px', fontSize: 13, cursor: 'pointer', marginBottom: 16, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}>
          <Plus size={14} /> Add session note
        </button>
      )}

      {composing && (
        <div style={{ background: theme.paper, border: `1px solid ${theme.line}`, padding: '14px', marginBottom: 16 }}>
          <div style={{ fontSize: 11, letterSpacing: '0.1em', textTransform: 'uppercase', color: theme.inkFaint, marginBottom: 8 }}>New session note — {new Date().toLocaleDateString('en-GB')}</div>
          <DictableText value={draft} onChange={setDraft} placeholder={`Notes from today's session with ${clientName.split(' ')[0]}...`} rows={6} autoFocus />
          <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
            <button onClick={() => { setComposing(false); setDraft(''); }} style={{ flex: 1, background: 'transparent', border: `1px solid ${theme.line}`, color: theme.inkSoft, padding: '9px', fontSize: 13, cursor: 'pointer' }}>Cancel</button>
            <button onClick={save} disabled={!draft.trim()} style={{ flex: 1, background: draft.trim() ? theme.ink : theme.line, color: theme.paper, border: 'none', padding: '9px', fontSize: 13, cursor: draft.trim() ? 'pointer' : 'not-allowed' }}>Save note</button>
          </div>
        </div>
      )}

      {notes.length === 0 ? (
        <EmptyState text="No session notes yet. Add one after your next session." />
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {[...notes].reverse().map(n => (
            <div key={n.id} style={{ background: theme.paper, border: `1px solid ${theme.line}`, borderLeft: `2px solid ${theme.accent}`, padding: '14px' }}>
              <div style={{ fontSize: 11, letterSpacing: '0.1em', textTransform: 'uppercase', color: theme.inkFaint, marginBottom: 8 }}>
                {new Date(n.date).toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' })}
              </div>
              <div style={{ fontFamily: '"Fraunces", serif', fontSize: 15, color: theme.ink, lineHeight: 1.6, whiteSpace: 'pre-wrap' }}>{n.content}</div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function CoachProfileView({ session, updateData, logout }) {
  const coach = session.data.coach;
  const [showSecurity, setShowSecurity] = useState(false);

  return (
    <div style={{ padding: '2rem 1.5rem', maxWidth: 600, margin: '0 auto' }}>
      <div style={{ fontSize: 11, letterSpacing: '0.25em', textTransform: 'uppercase', color: theme.inkFaint, marginBottom: 6 }}>Your practice</div>
      <h1 style={{ fontFamily: '"Fraunces", serif', fontWeight: 400, fontSize: '2rem', color: theme.ink, margin: 0, marginBottom: '1.5rem', letterSpacing: '-0.02em' }}>Profile</h1>

      <div style={{ background: theme.paper, border: `1px solid ${theme.line}`, padding: '20px', marginBottom: 20 }}>
        <div style={{ display: 'flex', gap: 14, marginBottom: 16 }}>
          <div style={{ width: 56, height: 56, borderRadius: '50%', background: theme.bgDeep, display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: '"Fraunces", serif', fontSize: 20, color: theme.accent }}>
            {coach.name.split(' ').map(n => n[0]).join('')}
          </div>
          <div style={{ flex: 1 }}>
            <div style={{ fontFamily: '"Fraunces", serif', fontSize: 20, color: theme.ink }}>{coach.name}</div>
            <div style={{ fontSize: 12, color: theme.inkFaint, marginBottom: 4 }}>{formatCredential(coach.credential)}</div>
            <div style={{ fontSize: 13, color: theme.inkSoft, fontStyle: 'italic', fontFamily: '"Fraunces", serif' }}>{coach.specialism}</div>
          </div>
        </div>
        <p style={{ fontSize: 14, color: theme.ink, lineHeight: 1.6, margin: 0, marginBottom: 12 }}>{coach.bio}</p>
        <div style={{ fontSize: 13, color: theme.inkSoft, borderTop: `1px solid ${theme.line}`, paddingTop: 12 }}>
          Session rate: <strong style={{ color: theme.ink, fontFamily: '"Fraunces", serif', fontSize: 15 }}>£{coach.rate}</strong>
        </div>
      </div>

      <Section title="Coaching style">
        <div style={{ background: theme.paper, border: `1px solid ${theme.line}`, padding: '14px' }}>
          {(() => {
            const archetype = recommendPersona(coach.axes || {});
            return archetype ? (
              <div style={{ background: theme.bg, border: `1px solid ${theme.line}`, borderLeft: `3px solid ${archetype.accent || theme.accent}`, padding: '12px 14px', marginBottom: 14 }}>
                <div style={{ fontSize: 10, letterSpacing: '0.2em', textTransform: 'uppercase', color: theme.inkFaint, marginBottom: 4 }}>Your coaching archetype</div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <span style={{ fontFamily: '"Fraunces", serif', fontSize: 22, color: archetype.accent || theme.accent }}>{archetype.glyph}</span>
                  <div>
                    <div style={{ fontFamily: '"Fraunces", serif', fontSize: 16, color: theme.ink }}>{archetype.name}</div>
                    <div style={{ fontSize: 12, color: theme.inkSoft }}>{archetype.tagline}</div>
                  </div>
                </div>
              </div>
            ) : null;
          })()}
          <AxesBar label="Directness" value={coach.axes?.directness ?? 0.5} />
          <AxesBar label="Warmth" value={coach.axes?.warmth ?? 0.5} />
          <AxesBar label="Structure" value={coach.axes?.structure ?? 0.5} />
          <AxesBar label="Pace" value={coach.axes?.energy ?? 0.5} />
        </div>
      </Section>

      <Section title="Account" icon={<User size={14} />}>
        <div style={{ background: theme.paper, border: `1px solid ${theme.line}`, padding: '14px', fontSize: 13, color: theme.inkSoft }}>
          <div>Logged in as <strong style={{ color: theme.ink }}>{session.email}</strong></div>
          <div style={{ fontSize: 11, marginTop: 6, color: theme.inkFaint }}>Account type: coach</div>
        </div>
      </Section>

      <Section title="Security" icon={<Shield size={14} />}>
        <button onClick={() => setShowSecurity(!showSecurity)}
          style={{ width: '100%', background: theme.paper, border: `1px solid ${theme.line}`, padding: '12px 14px', fontSize: 13, color: theme.ink, cursor: 'pointer', textAlign: 'left', display: 'flex', alignItems: 'center', gap: 10 }}>
          <Lock size={14} color={theme.success} />
          <span style={{ flex: 1 }}>How your data is protected</span>
          <ChevronRight size={14} style={{ transform: showSecurity ? 'rotate(90deg)' : 'none', transition: 'transform 200ms' }} />
        </button>
        {showSecurity && (
          <div style={{ background: theme.paper, border: `1px solid ${theme.line}`, borderTop: 'none', padding: '14px', fontSize: 12, color: theme.inkSoft, lineHeight: 1.7 }}>
            <div style={{ marginBottom: 8 }}>◈ Password: PBKDF2 with 210,000 iterations</div>
            <div style={{ marginBottom: 8 }}>◈ Your data: AES-GCM-256 encrypted</div>
            <div style={{ marginBottom: 8 }}>◈ Client data: You see signals only, never raw content</div>
          </div>
        )}
      </Section>

      <Section title="Session">
        <button onClick={logout} style={{ background: 'transparent', border: `1px solid ${theme.danger}`, color: theme.danger, padding: '10px 14px', fontSize: 13, cursor: 'pointer', width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}>
          <LogOut size={14} /> Log out
        </button>
      </Section>
    </div>
  );
}

// ============================================================================
// TAB BARS
// ============================================================================

function ClientTabBar({ tab, setTab, personaAccent, unread }) {
  return <TabBar tabs={[
    { id: 'home', icon: Home, label: 'Today' }, { id: 'chat', icon: MessageCircle, label: 'AI coach' },
    { id: 'goals', icon: Target, label: 'Habits' },
    { id: 'coaches', icon: Users, label: 'Human', badge: unread > 0 ? unread : null },
    { id: 'settings', icon: Settings, label: 'Settings' },
  ]} tab={tab} setTab={setTab} accent={personaAccent} />;
}

function CoachTabBar({ tab, setTab, unread }) {
  return <TabBar tabs={[
    { id: 'home', icon: Home, label: 'Today' },
    { id: 'clients', icon: Users, label: 'Clients', badge: unread > 0 ? unread : null },
    { id: 'briefs', icon: Inbox, label: 'Briefs' }, { id: 'profile', icon: User, label: 'Profile' },
  ]} tab={tab} setTab={setTab} accent={theme.accent} />;
}

function TabBar({ tabs, tab, setTab, accent }) {
  return (
    <div style={{ position: 'fixed', bottom: 0, left: 0, right: 0, background: theme.paper, borderTop: `1px solid ${theme.line}`, display: 'flex', paddingBottom: 'env(safe-area-inset-bottom)', maxWidth: 600, margin: '0 auto' }}>
      {tabs.map(t => {
        const Icon = t.icon;
        const active = tab === t.id;
        return (
          <button key={t.id} onClick={() => setTab(t.id)}
            style={{ flex: 1, background: 'transparent', border: 'none', padding: '10px 4px 12px', cursor: 'pointer', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4, color: active ? theme.ink : theme.inkFaint, borderTop: active ? `2px solid ${accent}` : '2px solid transparent', marginTop: -1, position: 'relative' }}>
            <div style={{ position: 'relative' }}>
              <Icon size={18} strokeWidth={active ? 2 : 1.5} />
              {t.badge && (
                <span style={{ position: 'absolute', top: -6, right: -10, background: theme.danger, color: theme.paper, fontSize: 9, fontWeight: 600, borderRadius: 10, padding: '2px 5px', minWidth: 16, textAlign: 'center', lineHeight: 1.2 }}>
                  {t.badge > 9 ? '9+' : t.badge}
                </span>
              )}
            </div>
            <span style={{ fontSize: 10, letterSpacing: '0.05em' }}>{t.label}</span>
          </button>
        );
      })}
    </div>
  );
}

function AppWithBoundary() {
  return (
    <ErrorBoundary>
      <App />
    </ErrorBoundary>
  );
}

export default AppWithBoundary;
